import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { getExams, getExam, createAttempt, submitWriting, submitReading, pollAttemptUntilMarked, getMyFetAttempts, getCalendarEvents, setCalendarEventAway } from '../api/exams';
import { getLandingConfig } from '../api/auth';
import FETProfileMenu from '../components/FETProfileMenu';
import FETWritingReportView from '../components/FETWritingReportView';
import ReadingOnboarding from './reading/ReadingOnboarding';
import ReadingHome from './reading/ReadingHome';
import Part1Screen from './reading/Part1Screen';
import Part2Screen from './reading/Part2Screen';
import Part3Screen from './reading/Part3Screen';
import Part4Screen from './reading/Part4Screen';
import Part5Screen from './reading/Part5Screen';
import ExamModeScreen from './reading/ExamModeScreen';
import ReadingExtras from './reading/ReadingExtras';
import ReadingExamPicker from './reading/ReadingExamPicker';

const CHECKOUT_RETURN_TO_KEY = 'checkout_return_to';
const DEFAULT_FET_COUNTDOWN_TARGET = '2026-12-30T09:00:00';
const EXAMS_PAGE_SIZE = 12;

const READINESS_LOOKBACK = 5;
const toDayKey = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const isWithinAwayWindow = (date, awayWindows) => {
  const key = toDayKey(date);
  return awayWindows.some(({ start, end }) => key >= start && key <= end);
};

const computeDashboardStats = (attempts, awayEvents = []) => {
  const empty = {
    loading: false,
    hasAttempts: false,
    attemptCount: 0,
    streakDays: 0,
    overallReadinessPct: null,
    writingPct: null,
    readingPct: null,
    listeningPct: null,
    // "Pending" means the user actually submitted something but the
    // marker hasn't finished yet. We surface that as "Marking…" in the
    // UI instead of the misleading "Not yet" — which made students
    // think their submitted exam never registered.
    writingPending: false,
    readingPending: false,
  };
  if (!Array.isArray(attempts) || !attempts.length) return empty;

  // "Away" windows the user has opted into — these are skipped (not reset, not incremented) when walking back.
  const awayWindows = (awayEvents || [])
    .filter((e) => e && e.user_away)
    .map((e) => ({ start: e.starts_at, end: e.ends_at }));

  // Streak: walk back day-by-day from today.
  const dayKeys = new Set(
    attempts
      .map((attempt) => toDayKey(attempt.started_at || attempt.completed_at))
      .filter(Boolean),
  );
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let streak = 0;
  let anchor = new Date(today);

  // Allow today OR yesterday as the streak anchor (skipping any away days at the start).
  while (anchor && isWithinAwayWindow(anchor, awayWindows)) {
    anchor = new Date(anchor.getTime() - 86400000);
  }
  if (!dayKeys.has(toDayKey(anchor))) {
    anchor = new Date(anchor.getTime() - 86400000);
    while (anchor && isWithinAwayWindow(anchor, awayWindows)) {
      anchor = new Date(anchor.getTime() - 86400000);
    }
    if (!dayKeys.has(toDayKey(anchor))) {
      anchor = null;
    }
  }
  if (anchor) {
    while (anchor) {
      const key = toDayKey(anchor);
      if (dayKeys.has(key)) {
        streak += 1;
      } else if (isWithinAwayWindow(anchor, awayWindows)) {
        // away day — preserve streak, don't increment
      } else {
        break;
      }
      anchor = new Date(anchor.getTime() - 86400000);
    }
  }

  // Per-skill readiness — average of the most recent N completed attempts.
  // We also track whether any submission is pending so the dashboard can
  // distinguish "no attempts" (Not yet) from "marker still running"
  // (Marking…). 'failed' counts as scored (the student tried, got a 0)
  // so a failed AI mark doesn't disappear from the readiness picture.
  const writingRatios = [];
  const readingRatios = [];
  let writingPending = false;
  let readingPending = false;
  attempts.forEach((attempt) => {
    const writingResponses = attempt.writing_responses || [];
    writingResponses.forEach((r) => {
      if (r.mark_status === 'done' || r.mark_status === 'failed') {
        const total = Number(r.total || 0);
        const max = Number(r.max_total || 0);
        if (max > 0) writingRatios.push(total / max);
      } else if (r.mark_status === 'pending' || r.mark_status === 'marking') {
        writingPending = true;
      }
    });
    const readingResponses = attempt.reading_responses || [];
    readingResponses.forEach((r) => {
      const pct = Number(r.percentage);
      if (Number.isFinite(pct)) {
        readingRatios.push(pct / 100);
      } else {
        readingPending = true;
      }
    });
  });

  const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
  const writingAvg = avg(writingRatios.slice(0, READINESS_LOOKBACK));
  const readingAvg = avg(readingRatios.slice(0, READINESS_LOOKBACK));

  const components = [writingAvg, readingAvg].filter((v) => v !== null);
  const overallAvg = components.length ? components.reduce((a, b) => a + b, 0) / components.length : null;

  const toPct = (ratio) => (ratio === null ? null : Math.round(ratio * 100));

  return {
    loading: false,
    hasAttempts: true,
    attemptCount: attempts.length,
    streakDays: streak,
    overallReadinessPct: toPct(overallAvg),
    writingPct: toPct(writingAvg),
    readingPct: toPct(readingAvg),
    listeningPct: null, // FET has no listening section yet
    writingPending,
    readingPending,
  };
};

const ACCENT_PALETTE = {
  green:  { fg: '#059669', bg: '#D1FAE5' },
  orange: { fg: '#C2410C', bg: '#FFEDD5' },
  blue:   { fg: '#0284C7', bg: '#E0F2FE' },
  purple: { fg: '#7C3AED', bg: '#EDE9FE' },
  red:    { fg: '#B91C1C', bg: '#FEE2E2' },
};
const accentFor = (name) => ACCENT_PALETTE[name] || ACCENT_PALETTE.green;
const ICON_PATHS = {
  calendar: 'M3 5a2 2 0 012-2h6a2 2 0 012 2v1h2v8a2 2 0 01-2 2H3a2 2 0 01-2-2V6h2V5z',
  crescent: 'M8 2a6 6 0 100 12A6 6 0 008 2zm.5 3v3.5L11 10l-.5.9L7 9V5z',
};
const formatEventDateRange = (startsAt, endsAt) => {
  if (!startsAt) return '';
  const start = new Date(startsAt);
  const end = endsAt ? new Date(endsAt) : null;
  const opts = { day: 'numeric', month: 'short' };
  const startStr = start.toLocaleDateString('en-GB', opts);
  if (!end || start.toDateString() === end.toDateString()) return startStr;
  const sameYear = start.getFullYear() === end.getFullYear();
  const endStr = end.toLocaleDateString('en-GB', sameYear ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
  return `${startStr} — ${endStr}`;
};
const computeDayStatus = (startsAt, endsAt) => {
  if (!startsAt) return { label: '', isActive: false, isPast: false };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const start = new Date(startsAt); start.setHours(0, 0, 0, 0);
  const end = endsAt ? new Date(endsAt) : start; end.setHours(0, 0, 0, 0);
  if (today < start) {
    const days = Math.round((start - today) / 86400000);
    return { label: days === 0 ? 'starts today' : `in ${days}d`, isActive: false, isPast: false };
  }
  if (today >= start && today <= end) {
    const totalDays = Math.round((end - start) / 86400000) + 1;
    const dayInto = Math.round((today - start) / 86400000) + 1;
    return { label: `Day ${dayInto} of ${totalDays}`, isActive: true, isPast: false };
  }
  return { label: 'completed', isActive: false, isPast: true };
};
const recommendationFor = (event) => {
  if (!event) return '';
  const m = Number(event.recommended_minutes_per_day || 0);
  if (m === 0) return 'Rest — your streak will be paused';
  if (m <= 15) return `Light review — ${m} min/day`;
  if (m <= 25) return `Adjusted hours — ${m} min/day`;
  return `${m} min/day`;
};

const FETLandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { examId: routeExamId } = useParams();
  const { user, loading, logout, refreshUser } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const pricingPath = `/pricing?return_to=${encodeURIComponent(`${location.pathname}${location.search}`)}`;
  const routeView = searchParams.get('mode') === 'practice'
    ? 'practice'
    : searchParams.get('mode') === 'select'
      ? 'select'
      : searchParams.get('mode') === 'section-select'
        ? 'section-select'
        : searchParams.get('mode') === 'reading'
          ? 'reading'
          : searchParams.get('mode') === 'reading-onboarding'
            ? 'reading-onboarding'
            : searchParams.get('mode') === 'reading-home'
              ? 'reading-home'
              : searchParams.get('mode') === 'reading-part-1'
                ? 'reading-part-1'
                : searchParams.get('mode') === 'reading-part-2'
                  ? 'reading-part-2'
                  : searchParams.get('mode') === 'reading-part-3'
                    ? 'reading-part-3'
                    : searchParams.get('mode') === 'reading-part-4'
                      ? 'reading-part-4'
                      : searchParams.get('mode') === 'reading-part-5'
                        ? 'reading-part-5'
                        : searchParams.get('mode') === 'reading-exam-picker'
                            ? 'reading-exam-picker'
                            : searchParams.get('mode') === 'reading-exam'
                            ? 'reading-exam'
                            : searchParams.get('mode') === 'reading-mistakes'
                              ? 'reading-mistakes'
                              : searchParams.get('mode') === 'reading-vocabulary'
                                ? 'reading-vocabulary'
                                : searchParams.get('mode') === 'reading-training'
                                  ? 'reading-training'
                                  : searchParams.get('mode') === 'reading-select'
                                    ? 'reading-select'
                                    : 'landing';
  const [currentView, setCurrentView] = useState(routeView);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1440));
  const [currentQuestion, setCurrentQuestion] = useState(16);
  const [timeLeft, setTimeLeft] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [exam, setExam] = useState(null);
  const [availableExams, setAvailableExams] = useState([]);
  const [availableExamsLoading, setAvailableExamsLoading] = useState(true);
  const [examsHasMore, setExamsHasMore] = useState(false);
  const [examsTotal, setExamsTotal] = useState(0);
  const [examsPage, setExamsPage] = useState(1);
  const [examsLoadingMore, setExamsLoadingMore] = useState(false);
  const [englishInviteOpen, setEnglishInviteOpen] = useState(true);
  const [resolvedExamId, setResolvedExamId] = useState('');
  const [attemptId, setAttemptId] = useState('');
  const [practiceLoading, setPracticeLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showSubmittedAnswers, setShowSubmittedAnswers] = useState(false);
  const [toast, setToast] = useState('');
  const [readingAnswers, setReadingAnswers] = useState({});
  const [readingResult, setReadingResult] = useState(null);
  const [readingSubmitting, setReadingSubmitting] = useState(false);
  const [readingAttemptId, setReadingAttemptId] = useState('');
  const [readingStarted, setReadingStarted] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // When createAttempt fails before the user has answered anything we surface
  // a clear, action-oriented error banner instead of a generic toast. The
  // backend distinguishes: feature_locked / insufficient_credits / on_break.
  const [attemptStartError, setAttemptStartError] = useState(null);
  // Banner that lets the user know we restored their previous unsaved draft.
  const [draftRestoredAt, setDraftRestoredAt] = useState(null);
  const [navDrawerOpen, setNavDrawerOpen] = useState(false);
  const [flaggedQuestions, setFlaggedQuestions] = useState({});
  const [fontPopoverOpen, setFontPopoverOpen] = useState(false);
  const [textSize, setTextSize] = useState('normal');
  const [fontStyle, setFontStyle] = useState('default');
  const [lineSpacing, setLineSpacing] = useState('normal');
  const [timeExpired, setTimeExpired] = useState(false);
  const [submissionPhase, setSubmissionPhase] = useState('idle');
  const fontPopoverRef = useRef(null);
  const navDrawerRef = useRef(null);

  // Seed landingConfig from localStorage so the countdown shows the
  // *real* admin-configured date on first paint instead of the hardcoded
  // DEFAULT fallback. Without this, every cold visit shows "224 days"
  // for ~3-5s (the duration of the /auth/landing-config/ round-trip)
  // and then jumps to the configured value — looks like a bug to the user.
  const [landingConfig, setLandingConfig] = useState(() => {
    try {
      const raw = window.localStorage.getItem('fet_landing_config_cache');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Accept entries up to 24h old — the landing config rarely
      // changes and a slightly stale value is far better UX than
      // flashing the wrong countdown.
      if (parsed?.savedAt && (Date.now() - parsed.savedAt) < 24 * 60 * 60 * 1000) {
        return parsed.config || null;
      }
    } catch { /* private mode / quota */ }
    return null;
  });
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [dashboardStats, setDashboardStats] = useState({
    loading: true,
    hasAttempts: false,
    attemptCount: 0,
    streakDays: 0,
    overallReadinessPct: null,
    writingPct: null,
    readingPct: null,
    listeningPct: null,
  });
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [activeBreakModal, setActiveBreakModal] = useState(null); // event object
  const [optInSaving, setOptInSaving] = useState(false);

  useEffect(() => {
    // Always refresh in the background so admin changes propagate.
    // The cached seed already provided an instant first paint above.
    getLandingConfig('fet')
      .then((cfg) => {
        setLandingConfig(cfg);
        try {
          window.localStorage.setItem('fet_landing_config_cache', JSON.stringify({
            config: cfg, savedAt: Date.now(),
          }));
        } catch { /* private mode / quota */ }
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (loading || !user) {
      if (!user && !loading) {
        setDashboardStats((prev) => ({ ...prev, loading: false, hasAttempts: false }));
        setCalendarEvents([]);
        setCalendarLoading(false);
      }
      return undefined;
    }
    let cancelled = false;
    setDashboardStats((prev) => ({ ...prev, loading: true }));
    setCalendarLoading(true);
    Promise.all([
      getMyFetAttempts().catch((err) => { console.error(err); return []; }),
      getCalendarEvents(6).catch((err) => { console.error(err); return []; }),
    ]).then(([attempts, events]) => {
      if (cancelled) return;
      setCalendarEvents(events || []);
      setCalendarLoading(false);
      setDashboardStats(computeDashboardStats(attempts || [], events || []));
    });
    return () => { cancelled = true; };
  }, [loading, user]);

  useEffect(() => {
    const configuredTarget = landingConfig?.countdown_enabled && landingConfig?.countdown_target
      ? landingConfig.countdown_target
      : null;
    const targetValue = configuredTarget || DEFAULT_FET_COUNTDOWN_TARGET;
    const targetDate = new Date(targetValue);

    if (Number.isNaN(targetDate.getTime())) {
      setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return undefined;
    }

    const syncCountdown = () => {
      const now = new Date();
      const diff = targetDate - now;

      if (diff > 0) {
        setCountdown({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000)
        });
        return;
      }

      if (configuredTarget) {
        const fallbackDate = new Date(DEFAULT_FET_COUNTDOWN_TARGET);
        const fallbackDiff = fallbackDate - now;
        if (fallbackDiff > 0) {
          setCountdown({
            days: Math.floor(fallbackDiff / (1000 * 60 * 60 * 24)),
            hours: Math.floor((fallbackDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
            minutes: Math.floor((fallbackDiff % (1000 * 60 * 60)) / (1000 * 60)),
            seconds: Math.floor((fallbackDiff % (1000 * 60)) / 1000)
          });
          return;
        }
      }

      setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    };

    syncCountdown();
    const interval = setInterval(() => {
      syncCountdown();
    }, 1000);
    return () => clearInterval(interval);
  }, [landingConfig]);

  // Practice timer
  useEffect(() => {
    if (currentView !== 'practice' || practiceLoading || submitting || result || timeLeft <= 0) return undefined;
    const timer = setInterval(() => setTimeLeft((value) => Math.max(0, value - 1)), 1000);
    return () => clearInterval(timer);
  }, [currentView, practiceLoading, submitting, result, timeLeft]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    // Block reading mode only when admin has it disabled in landing config.
    const readingEnabled = landingConfig?.sections?.reading;
    const isReadingRoute = routeView === 'reading' || routeView === 'reading-home' || routeView === 'reading-select' || routeView === 'reading-onboarding' || routeView === 'reading-part-1' || routeView === 'reading-part-2' || routeView === 'reading-part-3' || routeView === 'reading-part-4' || routeView === 'reading-part-5' || routeView === 'reading-exam' || routeView === 'reading-exam-picker' || routeView === 'reading-mistakes' || routeView === 'reading-vocabulary' || routeView === 'reading-training';
    if (isReadingRoute && readingEnabled === false) {
      setToast('Reading practice — coming soon. We\'ll let you know when it\'s ready.');
      navigate('/', { replace: true });
      setCurrentView('landing');
      return;
    }
    setCurrentView(routeView);
  }, [routeView, navigate, landingConfig, loading, user]);

  useEffect(() => {
    if (!loading && (currentView === 'practice' || currentView === 'reading') && !user) {
      const nextPath = routeExamId ? `/exam/${routeExamId}?mode=${currentView}` : `/?mode=${currentView}`;
      navigate(`/login?next=${encodeURIComponent(nextPath)}`, { replace: true });
    }
  }, [currentView, loading, navigate, routeExamId, user]);

  useEffect(() => {
    if (routeExamId) {
      window.sessionStorage.setItem(CHECKOUT_RETURN_TO_KEY, `/exam/${routeExamId}`);
    }
  }, [routeExamId]);

  const dismissEnglishInvite = () => {
    setEnglishInviteOpen(false);
  };

  const shareEnglishApp = async () => {
    const url = 'https://examhub.xyz/';
    const text = 'Help your friends prepare to pass the college intake exam — share this link with them.';
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Intake Exam',
          text,
          url,
        });
        return;
      }
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setToast('Intake exam link copied to clipboard.');
        return;
      }
      window.prompt('Copy this link:', url);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        setToast('Could not share the link right now.');
      }
    }
  };

  useEffect(() => {
    if (!toast) return undefined;
    const timeout = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(timeout);
  }, [toast]);

  useEffect(() => {
    setShowSubmittedAnswers(false);
  }, [result?.id]);

  useEffect(() => {
    if (!fontPopoverOpen) return undefined;

    const handleOutside = (event) => {
      if (fontPopoverRef.current && !fontPopoverRef.current.contains(event.target)) {
        setFontPopoverOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [fontPopoverOpen]);

  useEffect(() => {
    const body = document.body;
    body.classList.remove('fet-text-small', 'fet-text-normal', 'fet-text-large');
    body.classList.remove('fet-font-default', 'fet-font-serif', 'fet-font-dyslexic');
    body.classList.remove('fet-spacing-compact', 'fet-spacing-normal', 'fet-spacing-relaxed');

    body.classList.add(`fet-text-${textSize}`);
    body.classList.add(`fet-font-${fontStyle}`);
    body.classList.add(`fet-spacing-${lineSpacing}`);

    if (fontStyle === 'dyslexic' && !document.getElementById('fet-open-dyslexic-font')) {
      const link = document.createElement('link');
      link.id = 'fet-open-dyslexic-font';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.cdnfonts.com/css/open-dyslexic';
      document.head.appendChild(link);
    }
  }, [textSize, fontStyle, lineSpacing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `00:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const wordCount = (text) => text.trim() ? text.trim().split(/\s+/).length : 0;
  const clampToWordLimit = (text, limit) => {
    if (!limit || limit < 1) return text;
    const chunks = text.match(/\S+\s*/g);
    if (!chunks || chunks.length <= limit) return text;
    return chunks.slice(0, limit).join('').trimEnd();
  };
  const splitLines = (text = '') => text.split('\n').map((line) => line.trim()).filter(Boolean);
  const isMobile = viewportWidth <= 768;
  const isSmallMobile = viewportWidth <= 480;
  const isTablet = viewportWidth <= 1024;
  const theme = isDark ? {
    pageBg: '#020617',
    pageBgSoft: '#0f172a',
    pageBgAlt: '#111827',
    surface: '#0f172a',
    surfaceAlt: '#111827',
    surfaceSoft: '#1e293b',
    border: '#334155',
    text: '#F8FAFC',
    body: '#CBD5E1',
    muted: '#94A3B8',
    accent: '#818CF8',
    accentSoft: '#312e81',
    accentSoftBg: '#1e1b4b',
    shadow: '0 10px 30px rgba(2,6,23,.45)',
    overlayDot: 'rgba(129,140,248,0.12)',
    overlayOrb: 'rgba(99,102,241,0.18)',
    ctaBg: '#6366F1',
    ctaText: '#ffffff',
    chipBg: '#1e1b4b',
    chipBorder: '#4338ca',
    softBadgeBg: '#1e1b4b',
    softBadgeText: '#C7D2FE',
    progressTrack: '#334155',
    logoutText: '#CBD5E1',
  } : {
    pageBg: '#F9FAFB',
    pageBgSoft: '#EEF2FF',
    pageBgAlt: '#FFFFFF',
    surface: '#ffffff',
    surfaceAlt: '#FFFFFF',
    surfaceSoft: '#F8FAFC',
    border: '#E5E7EB',
    text: '#111827',
    body: '#374151',
    muted: '#9CA3AF',
    accent: '#4F46E5',
    accentSoft: '#EEF2FF',
    accentSoftBg: '#EEF2FF',
    shadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
    overlayDot: 'rgba(79,70,229,0.06)',
    overlayOrb: 'rgba(199,210,254,0.15)',
    ctaBg: '#4F46E5',
    ctaText: '#ffffff',
    chipBg: '#EEF2FF',
    chipBorder: '#4F46E5',
    softBadgeBg: '#F3F4F6',
    softBadgeText: '#374151',
    progressTrack: '#E5E7EB',
    logoutText: '#6B7280',
  };
  const accountControl = user ? (
    <FETProfileMenu user={user} onLogout={logout} showBalance={!isMobile} />
  ) : (
    <button
      type="button"
      onClick={() => navigate(`/login?next=${encodeURIComponent(`${location.pathname}${location.search}` || '/')}`)}
      aria-label="Open login"
      title="Login"
      style={{
        border: `1px solid ${theme.border}`,
        background: theme.surface,
        color: theme.body,
        borderRadius: '999px',
        width: '40px',
        height: '40px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: theme.shadow,
        flexShrink: 0
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M20 21a8 8 0 0 0-16 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    </button>
  );
  const heroBadge = landingConfig?.hero_badge?.en || 'FET EXAM PREP';
  const heroTitle = landingConfig?.hero_title?.en || 'Get ready to pass the FET exam';
  const heroSubtitle = landingConfig?.hero_subtitle?.en || 'Focused writing practice with a live countdown and a clear path to exam day.';
  const primaryCta = landingConfig?.primary_cta?.en || 'Start practice';
  const secondaryCta = landingConfig?.secondary_cta?.en || 'View plans';
  const configuredCountdownTarget = landingConfig?.countdown_enabled && landingConfig?.countdown_target
    ? landingConfig.countdown_target
    : null;
  const configuredCountdownDate = configuredCountdownTarget ? new Date(configuredCountdownTarget) : null;
  const resolvedCountdownTarget = configuredCountdownDate && configuredCountdownDate.getTime() > Date.now()
    ? configuredCountdownTarget
    : DEFAULT_FET_COUNTDOWN_TARGET;
  const countdownDate = new Date(resolvedCountdownTarget);
  const countdownActive = !Number.isNaN(countdownDate.getTime());
  const countdownDateLabel = countdownActive
    ? `${countdownDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })} — 08 Jan 27`
    : 'TBA';
  const part1Questions = exam?.questions?.filter((question) => question.part === 1) || [];
  const part2Questions = exam?.questions?.filter((question) => question.part === 2) || [];
  const part1Question = part1Questions[0] || null;
  const selectedPart2Id = selectedTopic || part2Questions[0]?.id || null;
  const selectedPart2Question = part2Questions.find((question) => question.id === selectedPart2Id) || null;
  const hasPart1 = Boolean(part1Question);
  const hasPart2 = part2Questions.length > 0;
  const totalSteps = Math.max(1, (hasPart1 ? 1 : 0) + (hasPart2 ? 1 : 0));
  const currentStep = hasPart1 ? (currentQuestion === 16 ? 1 : totalSteps) : 1;
  const currentPart = hasPart1 && currentStep === 1 ? '1' : hasPart2 ? '2' : '1';
  const showPart1View = hasPart1 && currentStep === 1;
  const visibleQuestion = showPart1View ? part1Question : (selectedPart2Question || part1Question);
  const isQuestionTwo = !showPart1View;
  const progressPercent = Math.round((currentStep / totalSteps) * 100);
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === totalSteps;
  // Block "Next" / "Submit" until the current visible question has at least
  // one character of text. Enforces "you can't skip a question without
  // answering it" the same way the reading screens do.
  const currentAnswerText = ((answers || {})[visibleQuestion?.id] || '').trim();
  const currentQuestionAnswered = currentAnswerText.length > 0;
  const readingParts = exam?.reading_parts || [];
  const writingReadyExams = availableExams.filter((item) => {
    if (typeof item?.has_writing_content === 'boolean') return item.has_writing_content;
    if (typeof item?.question_count === 'number') return item.question_count > 0;
    return (item?.questions || []).length > 0;
  });
  const readingReadyExams = availableExams.filter((item) => {
    if (typeof item?.has_reading_content === 'boolean') return item.has_reading_content;
    return (item?.reading_parts || []).some((part) => part.has_content && (part.question_count || 0) > 0);
  });
  const readingQuestionTotal = readingParts.reduce((sum, part) => sum + (part.question_count || 0), 0);
  const writingPart1Count = part1Questions.length;
  const writingPart2Count = part2Questions.length;
  const totalNavQuestions = readingQuestionTotal + writingPart1Count + writingPart2Count;

  const getQuestionLead = (question) => {
    if (!question) return [];
    const instructionLines = splitLines(question.instruction);
    if (instructionLines.length) return instructionLines;
    if (question.question_type === 'email' && question.email_body) return splitLines(question.email_body);
    if (question.prompt_heading) return [question.prompt_heading];
    return question.label ? [question.label] : [];
  };

  const getQuestionExtraText = (question) => {
    if (!question) return [];
    if (question.question_type === 'story' && question.story_opener) {
      return [question.story_opener];
    }
    if (question.question_type === 'email' && question.email_subject) {
      return [`Subject: ${question.email_subject}`];
    }
    return [];
  };

  const getQuestionOptionTitle = (question, index) => {
    const optionLetter = String.fromCharCode(65 + index);
    const baseTitle = question.prompt_title || question.label || `Option ${optionLetter}`;
    return baseTitle.trim().startsWith(`${optionLetter}:`) ? baseTitle : `${optionLetter}: ${baseTitle}`;
  };
  const getQuestionTypeLabel = (question) => ({
    email: 'Email',
    article: 'Article',
    story: 'Story',
    essay: 'Essay',
    picture: 'Picture prompt',
  }[question?.question_type] || 'Writing task');

  const getQuestionBullets = (question) => {
    if (!question) return [];
    if (question.notes?.length) return question.notes;
    if (question.prompt_items?.length) return question.prompt_items;
    return [];
  };

  const getWordInstruction = (question, fallback) => {
    if (!question) return fallback;
    if (question.write_instruction) return question.write_instruction;
    if (question.word_count) return `Write around ${question.word_count} words.`;
    return fallback;
  };

  const getExamDurationSeconds = (examData) => {
    const minutes = Number(examData?.time_mins || 45);
    return Math.max(1, minutes) * 60;
  };

  // Per-(user, exam) localStorage key for writing drafts. Saving as the user
  // types means a closed tab / accidental nav doesn't lose Q1/Q2 drafts. The
  // matching restore logic runs after the exam loads in the practice effect.
  const draftKey = (userId, examId) => `fet_writing_draft_${userId || 'anon'}_${examId || ''}`;
  const saveDraft = (userId, examId, answersToSave) => {
    if (!examId) return;
    try {
      const hasAny = Object.values(answersToSave || {}).some((v) => (v || '').trim().length > 0);
      if (!hasAny) {
        window.localStorage.removeItem(draftKey(userId, examId));
        return;
      }
      window.localStorage.setItem(draftKey(userId, examId), JSON.stringify({
        answers: answersToSave,
        savedAt: Date.now(),
      }));
    } catch { /* quota/private mode — ignore */ }
  };
  const loadDraft = (userId, examId) => {
    try {
      const raw = window.localStorage.getItem(draftKey(userId, examId));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.answers && typeof parsed.answers === 'object') return parsed;
      return null;
    } catch { return null; }
  };
  const clearDraft = (userId, examId) => {
    try { window.localStorage.removeItem(draftKey(userId, examId)); } catch {}
  };

  const updateAnswerWithLimit = (question, nextValue) => {
    if (!question?.id || timeExpired || submitting || result) return;
    const limitedValue = clampToWordLimit(nextValue, question.word_count);
    if (limitedValue !== nextValue && question.word_count) {
      setToast(`This answer is limited to ${question.word_count} words.`);
    }
    setAnswers((prev) => {
      const next = { ...prev, [question.id]: limitedValue };
      saveDraft(user?.id, resolvedExamId, next);
      return next;
    });
  };

  const buildNavigationSections = () => {
    const sections = [];
    let displayNumber = 1;

    if (readingQuestionTotal > 0) {
      const readingItems = [];
      readingParts.forEach((part) => {
        const count = part.question_count || 0;
        for (let index = 0; index < count; index += 1) {
          const id = `reading-${part.part_number}-${index + 1}`;
          readingItems.push({
            id,
            number: displayNumber,
            answered: false,
            current: false,
            flagged: Boolean(flaggedQuestions[id]),
            type: 'reading',
            partNumber: part.part_number,
          });
          displayNumber += 1;
        }
      });

      sections.push({
        key: 'reading',
        label: 'Reading',
        badge: `${readingItems.length} question${readingItems.length === 1 ? '' : 's'}`,
        items: readingItems,
      });
    }

    if (writingPart1Count > 0) {
      const items = part1Questions.map((question) => {
        const id = `writing-${question.id}`;
        const item = {
          id,
          number: displayNumber,
          answered: Boolean((answers[question.id] || '').trim()),
          current: showPart1View && visibleQuestion?.id === question.id,
          flagged: Boolean(flaggedQuestions[id]),
          type: 'writing-part1',
          questionId: question.id,
        };
        displayNumber += 1;
        return item;
      });

      sections.push({
        key: 'writing-part1',
        label: 'Writing',
        badge: `Part 1 · ${items.length} question${items.length === 1 ? '' : 's'}`,
        items,
      });
    }

    if (writingPart2Count > 0) {
      const items = part2Questions.map((question) => {
        const id = `writing-${question.id}`;
        const item = {
          id,
          number: displayNumber,
          answered: Boolean((answers[question.id] || '').trim()),
          current: !showPart1View && selectedPart2Id === question.id,
          flagged: Boolean(flaggedQuestions[id]),
          type: 'writing-part2',
          questionId: question.id,
        };
        displayNumber += 1;
        return item;
      });

      sections.push({
        key: 'writing-part2',
        label: 'Writing',
        badge: `Part 2 · ${items.length} question${items.length === 1 ? '' : 's'}`,
        items,
      });
    }

    return sections;
  };

  const navSections = buildNavigationSections();
  const navItems = navSections.flatMap((section) => section.items);
  const answeredCount = navItems.filter((item) => item.answered).length;
  const flaggedCount = navItems.filter((item) => item.flagged).length;
  const skippedCount = navItems.filter((item) => !item.answered).length;
  const firstUnanswered = navItems.find((item) => !item.answered);
  const answeredPercent = totalNavQuestions ? Math.round((answeredCount / totalNavQuestions) * 100) : 0;
  const examTimerTone = timeExpired
    ? { label: 'Exam Timer', status: 'Time Ended', border: '#FCA5A5', badgeBg: '#FEF2F2', badgeText: '#B91C1C', timeColor: '#991B1B', shadow: '0 12px 26px rgba(15,23,42,.10)' }
    : timeLeft <= 300
      ? { label: 'Exam Timer', status: 'Critical', border: '#FECACA', badgeBg: '#FEF2F2', badgeText: '#B91C1C', timeColor: '#B91C1C', shadow: '0 12px 26px rgba(220,38,38,.10)' }
      : timeLeft <= 900
        ? { label: 'Exam Timer', status: 'Watch Time', border: '#FDE68A', badgeBg: '#FFFBEB', badgeText: '#B45309', timeColor: '#B45309', shadow: '0 12px 26px rgba(217,119,6,.10)' }
        : { label: 'Exam Timer', status: 'Active', border: '#C7D2FE', badgeBg: '#EEF2FF', badgeText: '#4338CA', timeColor: isDark ? '#E5E7EB' : '#312E81', shadow: '0 12px 26px rgba(79,70,229,.08)' };

  const resolveExam = async () => {
    const explicitExamId = routeExamId || searchParams.get('examId') || process.env.REACT_APP_FET_EXAM_ID;
    if (explicitExamId) return getExam(explicitExamId);

    const exams = await getExams({ family: 'general' });
    const candidate = exams[0];
    if (!candidate) {
      throw new Error('No exam is configured for frontendFET.');
    }
    return getExam(candidate.id);
  };

  const resolveReadingExam = async () => {
    // Fast path: when the URL carries an explicit examId — which is true
    // for the entire dashboard → onboarding → reading-home flow — load
    // exactly that exam and return. The previous behaviour was to ALSO
    // walk through every other available exam looking for one with
    // reading content; with 20+ writing-shell exams in the library that
    // turned a single getExam into 20+ sequential round-trips and made
    // the reading screen take 5+ seconds to open.
    const explicitExamId = searchParams.get('examId') || routeExamId || process.env.REACT_APP_FET_EXAM_ID;
    if (explicitExamId) {
      return getExam(explicitExamId);
    }

    // No explicit examId (e.g. user opened reading directly without
    // selecting one) — fall back to the candidate scan, but use the new
    // 5-part content shape (`gaps` / `questions` / `items`) when checking
    // for reading content so we don't re-introduce the slow path just
    // because the backend's old question_count returns 0 for those keys.
    const candidateIds = [];
    (availableExams || []).forEach((item) => {
      if (item?.id && !candidateIds.includes(item.id)) candidateIds.push(item.id);
    });
    if (!candidateIds.length) {
      const exams = await getExams({ family: 'general' });
      exams.forEach((item) => {
        if (item?.id && !candidateIds.includes(item.id)) candidateIds.push(item.id);
      });
    }
    const hasAnyReading = (examData) => (examData?.reading_parts || []).some((part) => {
      if (!part?.has_content) return false;
      if ((part.question_count || 0) > 0) return true;
      const c = part.content || {};
      const n = part.part_number;
      if (n === 1) return Array.isArray(c.signs) && c.signs.length > 0;
      if (n === 2) return Array.isArray(c.gaps) && c.gaps.length > 0;
      if (n === 3) return Array.isArray(c.questions) && c.questions.length > 0;
      if (n === 4) return Array.isArray(c.items) && c.items.length > 0;
      if (n === 5) return Array.isArray(c.questions) && c.questions.length > 0;
      return false;
    });
    for (const id of candidateIds) {
      try {
        const examData = await getExam(id);
        if (hasAnyReading(examData)) return examData;
      } catch (error) {
        console.error(error);
      }
    }
    throw new Error('No reading exam with content is available right now.');
  };

  useEffect(() => {
    let cancelled = false;

    const loadAvailableExams = async () => {
      if (!cancelled) {
        setAvailableExamsLoading(true);
      }
      try {
        const data = await getExams({ family: 'general', page: 1, page_size: EXAMS_PAGE_SIZE });
        if (cancelled) return;
        if (Array.isArray(data)) {
          setAvailableExams(data);
          setExamsHasMore(false);
          setExamsTotal(data.length);
          setExamsPage(1);
        } else {
          setAvailableExams(data.results || []);
          setExamsHasMore(Boolean(data.has_next));
          setExamsTotal(Number(data.count) || 0);
          setExamsPage(Number(data.page) || 1);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setAvailableExams([]);
          setExamsHasMore(false);
          setExamsTotal(0);
          setExamsPage(1);
        }
      } finally {
        if (!cancelled) {
          setAvailableExamsLoading(false);
        }
      }
    };

    loadAvailableExams();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadMoreExams = async () => {
    if (examsLoadingMore || !examsHasMore) return;
    setExamsLoadingMore(true);
    try {
      const nextPage = examsPage + 1;
      const data = await getExams({ family: 'general', page: nextPage, page_size: EXAMS_PAGE_SIZE });
      if (Array.isArray(data)) {
        setExamsHasMore(false);
        return;
      }
      setAvailableExams((prev) => [...prev, ...(data.results || [])]);
      setExamsHasMore(Boolean(data.has_next));
      setExamsTotal(Number(data.count) || 0);
      setExamsPage(Number(data.page) || nextPage);
    } catch (error) {
      console.error(error);
      setToast('Could not load more exams. Try again in a moment.');
    } finally {
      setExamsLoadingMore(false);
    }
  };

  const startAttempt = async (examIdToUse) => {
    const attempt = await createAttempt(examIdToUse, 'practice', 'writing');
    setAttemptId(attempt.id);
    return attempt.id;
  };

  const startReadingAttempt = async (examIdToUse) => {
    const attempt = await createAttempt(examIdToUse, 'practice', 'reading');
    setReadingAttemptId(attempt.id);
    return attempt.id;
  };

  useEffect(() => {
    if (currentView !== 'practice' || loading || !user) return undefined;

    let cancelled = false;
    const initPractice = async () => {
      setPracticeLoading(true);
      setAttemptStartError(null);
      try {
        const examData = await resolveExam();
        if (cancelled) return;
        const shouldStartFreshAttempt = !attemptId || resolvedExamId !== examData.id;
        setExam(examData);
        setResolvedExamId(examData.id);
        const firstPart2 = examData.questions?.filter((question) => question.part === 2)?.[0] || null;

        if (shouldStartFreshAttempt) {
          setTimeLeft(getExamDurationSeconds(examData));
          setTimeExpired(false);
          setSubmissionPhase('idle');
          setSelectedTopic(firstPart2?.id || null);
          setAttemptId(null);
          setPracticeLoading(false);
          // Restore unsaved draft (Q1 / Q2 text) if there is one. This is what
          // makes "wrote Q1, navigated away, came back" work — the text the
          // user typed survives even though the backend never received it.
          const draft = loadDraft(user?.id, examData.id);
          if (draft && draft.answers) {
            setAnswers(draft.answers);
            setDraftRestoredAt(draft.savedAt || Date.now());
            // If they had typed in a Part 2 option, jump them to that topic
            const draftedPart2Id = (examData.questions || []).filter((q) => q.part === 2).find((q) => (draft.answers[q.id] || '').trim());
            if (draftedPart2Id) setSelectedTopic(draftedPart2Id.id);
          } else {
            setAnswers({});
            setDraftRestoredAt(null);
          }
          startAttempt(examData.id).catch((attemptError) => {
            console.error(attemptError);
            if (cancelled) return;
            const data = attemptError?.response?.data || {};
            const httpStatus = attemptError?.response?.status;
            const code = data.code;
            const backendMessage = data.error || data.detail || '';

            if (code === 'on_break') {
              navigate('/', { replace: true });
              const blockingEvent = data.event;
              if (blockingEvent) {
                const matched = (calendarEvents || []).find((e) => e.id === blockingEvent.id);
                setActiveBreakModal(matched || { ...blockingEvent, accent: 'blue', recommended_minutes_per_day: 0, hint: '', description: 'You marked yourself as away during this break. Toggle the option below to resume practice.', user_away: true });
              }
              setToast(backendMessage || 'You\'re on a break right now.');
              return;
            }
            if (code === 'feature_locked' || code === 'plan_required') {
              setAttemptStartError({
                code,
                tone: 'plan',
                title: 'Plan upgrade required',
                message: backendMessage || 'Your current plan does not include writing access. Upgrade to start this exam.',
                ctaLabel: 'View plans',
                onCta: () => navigate(`/pricing?locked=writing&return_to=${encodeURIComponent(`${location.pathname}${location.search}`)}`),
              });
              return;
            }
            if (code === 'insufficient_credits' || httpStatus === 402) {
              setAttemptStartError({
                code: 'insufficient_credits',
                tone: 'credits',
                title: 'Not enough AI credits',
                message: backendMessage || 'Starting a writing attempt costs 1 AI credit. Top up your wallet to continue.',
                ctaLabel: 'Buy AI credits',
                onCta: () => navigate(`/pricing?locked=credits&return_to=${encodeURIComponent(`${location.pathname}${location.search}`)}`),
              });
              return;
            }
            setAttemptStartError({
              code: 'unknown',
              tone: 'error',
              title: 'Could not start the exam',
              message: backendMessage || 'Something went wrong while preparing your attempt. Try again in a moment.',
              ctaLabel: 'Try again',
              onCta: () => { setAttemptStartError(null); setReloadKey((k) => k + 1); },
            });
          });
          return;
        } else {
          setSelectedTopic((prev) => prev || firstPart2?.id || null);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          const code = error?.response?.data?.code;
          if (code === 'feature_locked' || code === 'plan_required') {
            navigate(`/pricing?locked=writing&return_to=${encodeURIComponent(`${location.pathname}${location.search}`)}`, { replace: true });
            return;
          }
          if (code === 'on_break') {
            // Bounce back to landing and show the break details modal
            navigate('/', { replace: true });
            const blockingEvent = error?.response?.data?.event;
            if (blockingEvent) {
              const matched = (calendarEvents || []).find((e) => e.id === blockingEvent.id);
              setActiveBreakModal(matched || { ...blockingEvent, accent: 'blue', recommended_minutes_per_day: 0, hint: '', description: 'You marked yourself as away during this break. Toggle the option below to resume practice.', user_away: true });
            }
            setToast(error?.response?.data?.error || 'You\'re on a break right now.');
            return;
          }
          setToast(error?.response?.data?.error || 'Could not prepare the FET writing attempt right now.');
        }
      } finally {
        if (!cancelled) setPracticeLoading(false);
      }
    };

    initPractice();
    return () => { cancelled = true; };
  }, [currentView, loading, user, reloadKey, routeExamId, location.search]);

  useEffect(() => {
    if (currentView !== 'reading' || loading || !user) return undefined;

    let cancelled = false;
    const initReading = async () => {
      setPracticeLoading(true);
      try {
        const examData = await resolveReadingExam();
        if (cancelled) return;
        setExam(examData);
        setResolvedExamId(examData.id);
        setReadingAnswers({});
        setReadingResult(null);
        setReadingStarted(false);
        const attempt = await startReadingAttempt(examData.id);
        if (!cancelled) setReadingAttemptId(attempt);
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setToast(error?.response?.data?.error || 'Could not prepare the FET reading test right now.');
        }
      } finally {
        if (!cancelled) setPracticeLoading(false);
      }
    };

    initReading();
    return () => { cancelled = true; };
  }, [currentView, loading, user, reloadKey, routeExamId, location.search]);

  // reading-home + reading-part-*: resolve a reading-ready exam without starting an attempt yet.
  // The Part screens create their own attempt at submit time.
  useEffect(() => {
    const isReadingExamView = currentView === 'reading-home' || currentView === 'reading-part-1' || currentView === 'reading-part-2' || currentView === 'reading-part-3' || currentView === 'reading-part-4' || currentView === 'reading-part-5' || currentView === 'reading-exam' || currentView === 'reading-mistakes' || currentView === 'reading-vocabulary' || currentView === 'reading-training';
    if (!isReadingExamView || loading || !user) return undefined;
    let cancelled = false;
    const initReadingHome = async () => {
      setPracticeLoading(true);
      try {
        const examData = await resolveReadingExam();
        if (cancelled) return;
        setExam(examData);
        setResolvedExamId(examData.id);
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setToast(error?.response?.data?.error || 'No reading exam with content is available yet. Please add reading content from the admin panel.');
        }
      } finally {
        if (!cancelled) setPracticeLoading(false);
      }
    };
    initReadingHome();
    return () => { cancelled = true; };
  }, [currentView, loading, user, reloadKey, routeExamId, location.search]);

  const openPractice = (targetExamId = routeExamId || resolvedExamId || availableExams[0]?.id || process.env.REACT_APP_FET_EXAM_ID || '') => {
    if (loading) return;
    if (!targetExamId) {
      setToast('No FET exam is available right now.');
      return;
    }
    if (!user) {
      const nextPath = `/exam/${targetExamId}?mode=practice`;
      navigate(`/login?next=${encodeURIComponent(nextPath)}`);
      return;
    }
    navigate(`/exam/${targetExamId}?mode=practice`);
  };
  const openReading = () => {
    setToast('Reading practice — coming soon. We\'ll let you know when it\'s ready.');
  };
  const openExamPicker = () => {
    // Take the student to their dashboard first — they see what's pending,
    // recommended next, and per-skill progress before being asked to pick a
    // specific paper. Cleaner than dumping the full exam catalogue on them.
    navigate('/dashboard');
  };
  const openSectionWriting = () => {
    setCurrentView('select');
    navigate('/?mode=select');
  };
  const openSectionReading = () => {
    // Reading entry: pick the exam paper first → then onboarding (level +
    // Arabic prefs) → then reading-home for that paper. The picker keeps the
    // examId in the URL so it threads through the rest of the flow.
    setCurrentView('reading-exam-picker');
    navigate('/?mode=reading-exam-picker');
  };
  const openSectionSpeaking = () => {
    setToast('Speaking practice — coming soon. We\'ll let you know when it\'s ready.');
  };
  const backToLanding = () => navigate(routeExamId ? `/exam/${routeExamId}` : '/');

  const withTimeout = (promise, ms = 15000) => Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('Writing submission timed out.')), ms)),
  ]);

  const buildResponsesForSubmission = ({ enforceRequired = true } = {}) => {
    const part1Text = part1Question ? clampToWordLimit(answers[part1Question.id] || '', part1Question.word_count).trim() : '';
    const answeredPart2Questions = part2Questions
      .map((question) => ({
        question,
        text: clampToWordLimit(answers[question.id] || '', question.word_count).trim(),
      }))
      .filter((entry) => entry.text.length >= 5);

    if (enforceRequired) {
      if (part1Question && part1Text.length < 5) {
        return { error: 'Question 1 is required before submitting.', responses: [] };
      }
      if (part2Questions.length && answeredPart2Questions.length === 0) {
        return { error: 'Please answer one Question 2 option before submitting.', responses: [] };
      }
      if (answeredPart2Questions.length > 1) {
        return { error: 'Please answer only one Question 2 option before submitting.', responses: [] };
      }
    }

    const responses = [];
    if (part1Question && part1Text.length >= 5) {
      responses.push({ question_id: part1Question.id, text: part1Text });
    }

    const preferredPart2 = answeredPart2Questions.find((entry) => entry.question.id === selectedPart2Id) || answeredPart2Questions[0];
    if (preferredPart2) {
      responses.push({
        question_id: preferredPart2.question.id,
        text: preferredPart2.text,
      });
    }

    return { responses, error: responses.length ? '' : 'No valid writing responses found.' };
  };

  const handleSubmit = async () => {
    if (!attemptId || !exam) {
      setToast('The writing attempt is still being prepared. Please wait a moment.');
      return;
    }

    const { responses, error } = buildResponsesForSubmission({ enforceRequired: true });
    if (error) {
      if (timeExpired) {
        setResult({
          id: 'timeout-blocked',
          mark_status: 'blocked',
          question_label: 'FET Writing Test',
          title: 'Time ended, attempt again!',
          blocked_reason: error,
          blocked_detail: 'A report cannot be generated because the required writing tasks were not completed before time ran out.',
          suggestion: 'Please start a new attempt and answer Question 1 plus one Question 2 option before submitting.',
        });
      }
      setToast(error);
      return;
    }

    setSubmitting(true);
    setTimeExpired(true);
    setSubmissionPhase('submitting');
    setResult(null);

    try {
      await withTimeout(submitWriting(attemptId, responses));
      // Submission landed on the server — drop the local draft so the next
      // visit doesn't restore stale text on top of a fresh attempt.
      clearDraft(user?.id, resolvedExamId);
      setDraftRestoredAt(null);
      setSubmissionPhase('generating');
      await refreshUser().catch(() => {});

      const stop = pollAttemptUntilMarked(
        attemptId,
        (attempt) => {
          const writingResponses = attempt.writing_responses || [];
          const writingReport = attempt.fet_writing_report || attempt.writing_report;
          if (writingReport?.mark_status === 'done' || writingReport?.mark_status === 'failed') {
            stop();
            refreshUser().catch(() => {});
            setResult(
              writingReport
                ? {
                    ...writingReport,
                    writing_responses: writingResponses,
                  }
                : null,
            );
            setSubmitting(false);
            setSubmissionPhase('idle');
          }
        },
        (error) => {
          console.error(error);
          setSubmitting(false);
          setSubmissionPhase('idle');
          setToast('Writing was submitted, but the AI result is taking longer than expected.');
        },
      );
    } catch (error) {
      console.error(error);
      if (error?.response?.data?.code === 'ai_marking_locked') {
        navigate(`/pricing?locked=writing&return_to=${encodeURIComponent(`${location.pathname}${location.search}`)}`);
      } else if (error?.message === 'Writing submission timed out.') {
        setToast('Writing submission is taking too long to start. Please try again.');
      } else {
        setToast(error?.response?.data?.error || 'Could not submit writing right now.');
      }
      setSubmitting(false);
      setSubmissionPhase('idle');
    }
  };

  useEffect(() => {
    if (currentView === 'practice' && timeLeft <= 0 && !result) {
      setTimeExpired(true);
    }
  }, [currentView, timeLeft, result]);

  const handleTryAgain = async () => {
    if (!resolvedExamId) return;
    clearDraft(user?.id, resolvedExamId);
    setDraftRestoredAt(null);
    setAnswers({});
    setResult(null);
    setCurrentQuestion(16);
    setSelectedTopic(part2Questions[0]?.id || null);
    setSubmitting(false);
    setSubmissionPhase('idle');
    setTimeExpired(false);
    setPracticeLoading(true);
    setAttemptId(null);
    try {
      const freshExam = await resolveExam();
      setExam(freshExam);
      setResolvedExamId(freshExam.id);
      setTimeLeft(getExamDurationSeconds(freshExam));
      setSelectedTopic(freshExam.questions?.filter((question) => question.part === 2)?.[0]?.id || null);
      setPracticeLoading(false);
      startAttempt(freshExam.id).catch((error) => {
        console.error(error);
        setToast('The new writing attempt is still preparing. Please wait a moment.');
      });
      return;
    } catch (error) {
      console.error(error);
      setToast('Could not start a new writing attempt right now.');
    } finally {
      setPracticeLoading(false);
    }
  };

  const toggleFlag = (itemId) => {
    setFlaggedQuestions((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const navigateToNavItem = (item) => {
    if (item.type === 'reading') {
      if (resolvedExamId) {
        navigate(`/exam/${resolvedExamId}?mode=reading`);
      } else {
        setToast('Reading test is loading. Please try again.');
      }
      setNavDrawerOpen(false);
      return;
    }

    if (item.type === 'writing-part1') {
      setCurrentQuestion(16);
    } else {
      setCurrentQuestion(17);
      if (item.questionId) setSelectedTopic(item.questionId);
    }

    setNavDrawerOpen(false);
    requestAnimationFrame(() => {
      document.getElementById('fet-question-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const inputLocked = timeExpired || submitting || Boolean(result);

  const setReadingAnswer = (partNum, key, val) => {
    setReadingAnswers((prev) => ({ ...prev, [partNum]: { ...(prev[partNum] || {}), [key]: val } }));
  };

  const handleSubmitReading = async () => {
    if (!readingAttemptId) return;
    setReadingSubmitting(true);
    try {
      const res = await submitReading(readingAttemptId, readingAnswers);
      setReadingResult(res);
    } catch (error) {
      console.error(error);
      setToast(error?.response?.data?.error || 'Could not submit the reading test right now.');
    } finally {
      setReadingSubmitting(false);
    }
  };

  const renderReadingPart = (part) => {
    const c = part.content || {};
    const pn = part.part_number;
    const partAnswers = readingAnswers[pn] || {};

    let inner = null;

    if (pn === 1) {
      inner = (c.signs || []).map((sign, si) => (
        <div key={si} style={{ background: theme.surfaceAlt, border: `1px solid ${theme.border}`, borderRadius: 16, padding: 16, marginBottom: 12 }}>
          <div style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 12, padding: '12px 14px', fontSize: 14, color: theme.text, marginBottom: 12, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{sign.text}</div>
          <div style={{ fontSize: 12, color: theme.body, marginBottom: 8 }}>What does this sign mean?</div>
          {['A', 'B', 'C'].map((opt) => (
            <label key={opt} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', marginBottom: 8, borderRadius: 12, border: `1px solid ${partAnswers[`sign_${si}`] === opt ? theme.accent : theme.border}`, background: partAnswers[`sign_${si}`] === opt ? theme.accentSoftBg : '#fff', cursor: 'pointer' }}>
              <input type="radio" name={`reading-p1-${si}`} checked={partAnswers[`sign_${si}`] === opt} onChange={() => setReadingAnswer(1, `sign_${si}`, opt)} style={{ accentColor: theme.accent, marginTop: 2 }} />
              <span style={{ fontSize: 14, color: theme.text, lineHeight: 1.6 }}><strong>{opt}.</strong> {sign[`opt${opt}`]}</span>
            </label>
          ))}
        </div>
      ));
    } else if (pn === 2) {
      inner = (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
          <div>
            {(c.people || []).map((person, pi) => (
              <div key={pi} style={{ background: theme.surfaceAlt, border: `1px solid ${theme.border}`, borderRadius: 16, padding: 14, marginBottom: 10 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: theme.text, marginBottom: 6 }}>{person.name}</div>
                <div style={{ fontSize: 13, color: theme.body, lineHeight: 1.65, marginBottom: 10 }}>{person.desc}</div>
                <select className="fc" value={partAnswers[`person_${pi}`] || ''} onChange={(e) => setReadingAnswer(2, `person_${pi}`, e.target.value)}>
                  <option value="">Choose an activity</option>
                  {(c.activities || []).map((a) => <option key={a.id} value={a.id}>{a.id}: {a.title}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div>
            {(c.activities || []).map((a) => (
              <div key={a.id} style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 16, padding: 14, marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: theme.accent, marginBottom: 4 }}>{a.id}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: theme.text, marginBottom: 6 }}>{a.title}</div>
                <div style={{ fontSize: 13, color: theme.body, lineHeight: 1.6 }}>{a.text}</div>
              </div>
            ))}
          </div>
        </div>
      );
    } else if (pn === 3) {
      inner = (
        <>
          <div style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 16, padding: 16, marginBottom: 14, fontSize: 14, lineHeight: 1.85, color: theme.text, maxHeight: 280, overflowY: 'auto' }}>
            {(c.passage || '').split('\n\n').map((p, i) => <p key={i} style={{ marginBottom: 10 }}>{p}</p>)}
          </div>
          {(c.statements || []).map((s, si) => (
            <div key={si} style={{ background: theme.surfaceAlt, border: `1px solid ${theme.border}`, borderRadius: 16, padding: 14, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 14, flex: 1, color: theme.text, lineHeight: 1.6 }}>{si + 1}. {s.text}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {['T', 'F'].map((v) => (
                  <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '8px 14px', border: `1px solid ${partAnswers[`stmt_${si}`] === v ? theme.accent : theme.border}`, borderRadius: 12, fontSize: 13, fontWeight: 700, background: '#fff' }}>
                    <input type="radio" name={`reading-p3-${si}`} checked={partAnswers[`stmt_${si}`] === v} onChange={() => setReadingAnswer(3, `stmt_${si}`, v)} style={{ accentColor: theme.accent }} />
                    {v === 'T' ? 'True' : 'False'}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </>
      );
    } else if (pn === 4) {
      inner = (
        <>
          {c.title && <div style={{ fontSize: 18, fontWeight: 800, color: theme.text, marginBottom: 12 }}>{c.title}</div>}
          <div style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 16, padding: 16, marginBottom: 14, fontSize: 14, lineHeight: 1.85, color: theme.text, maxHeight: 280, overflowY: 'auto' }}>
            {(c.passage || '').split('\n\n').map((p, i) => <p key={i} style={{ marginBottom: 10 }}>{p}</p>)}
          </div>
          {(c.questions || []).map((q, qi) => (
            <div key={qi} style={{ background: theme.surfaceAlt, border: `1px solid ${theme.border}`, borderRadius: 16, padding: 14, marginBottom: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.text, marginBottom: 10, lineHeight: 1.6 }}>{qi + 1}. {q.text}</div>
              {['A', 'B', 'C', 'D'].filter((o) => q[`opt${o}`]).map((o) => (
                <label key={o} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', marginBottom: 8, borderRadius: 12, border: `1px solid ${partAnswers[`q_${qi}`] === o ? theme.accent : theme.border}`, background: partAnswers[`q_${qi}`] === o ? theme.accentSoftBg : '#fff', cursor: 'pointer' }}>
                  <input type="radio" name={`reading-p4-${qi}`} checked={partAnswers[`q_${qi}`] === o} onChange={() => setReadingAnswer(4, `q_${qi}`, o)} style={{ accentColor: theme.accent, marginTop: 2 }} />
                  <span style={{ fontSize: 14, color: theme.text, lineHeight: 1.6 }}><strong>{o}.</strong> {q[`opt${o}`]}</span>
                </label>
              ))}
            </div>
          ))}
        </>
      );
    } else if (pn === 5) {
      let txt = c.text || '';
      (c.blanks || []).forEach((_, bi) => { txt = txt.replace(`[${bi + 1}]`, `__BLANK_${bi}__`); });
      inner = (
        <div style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 16, padding: 16, fontSize: 14, lineHeight: 2.5, color: theme.text }}>
          {txt.split('__BLANK_').map((seg, i) => {
            if (i === 0) return <span key={i}>{seg}</span>;
            const [idxStr, ...rest] = seg.split('__');
            const bi = parseInt(idxStr, 10);
            const b = (c.blanks || [])[bi] || {};
            return (
              <React.Fragment key={i}>
                <select style={{ fontSize: 12, border: '1px solid #c7d2fe', padding: '4px 8px', background: '#eef2ff', fontWeight: 700, color: theme.accent, borderRadius: 8 }} value={partAnswers[`blank_${bi}`] || ''} onChange={(e) => setReadingAnswer(5, `blank_${bi}`, e.target.value)}>
                  <option value="">___</option>
                  {['A', 'B', 'C', 'D'].filter((o) => b[`opt${o}`]).map((o) => <option key={o} value={o}>{o}) {b[`opt${o}`]}</option>)}
                </select>
                <span>{rest.join('__')}</span>
              </React.Fragment>
            );
          })}
        </div>
      );
    } else if (pn === 6) {
      let txt = c.text || '';
      (c.answers || []).forEach((_, ai) => { txt = txt.replace(`[${ai + 1}]`, `__OPEN_${ai}__`); });
      inner = (
        <div style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 16, padding: 16, fontSize: 14, lineHeight: 2.7, color: theme.text }}>
          {txt.split('__OPEN_').map((seg, i) => {
            if (i === 0) return <span key={i}>{seg}</span>;
            const [idxStr, ...rest] = seg.split('__');
            const ai = parseInt(idxStr, 10);
            return (
              <React.Fragment key={i}>
                <input type="text" style={{ fontSize: 12, border: '1px solid #c7d2fe', padding: '4px 8px', width: 90, fontWeight: 700, color: theme.accent, background: '#eef2ff', textAlign: 'center', borderRadius: 8, outline: 'none' }} placeholder={`${ai + 1}`} value={partAnswers[`open_${ai}`] || ''} onChange={(e) => setReadingAnswer(6, `open_${ai}`, e.target.value)} />
                <span>{rest.join('__')}</span>
              </React.Fragment>
            );
          })}
        </div>
      );
    }

    return (
      <div key={part.id} className="fet-surface" style={{ borderRadius: 24, padding: isMobile ? '20px 16px' : '24px', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', padding: '7px 12px', borderRadius: 999, background: theme.accentSoftBg, color: theme.accent, fontSize: 12, fontWeight: 800 }}>{part.type_name || `Part ${pn}`}</span>
          <span style={{ fontSize: 12, color: theme.body }}>{part.description}</span>
        </div>
        {inner}
      </div>
    );
  };

  // Landing Page
  if (currentView === 'landing') {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse 900px 600px at 8% -10%, rgba(91,82,240,.18) 0%, transparent 60%), radial-gradient(ellipse 700px 500px at 95% 105%, rgba(244,114,182,.14) 0%, transparent 60%), linear-gradient(180deg, #F4F1FB 0%, #EBE6F7 100%)',
        overflow: 'hidden',
        position: 'relative',
        color: '#15142A',
        fontFamily: "'DM Sans', sans-serif"
      }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');

          .fet-bento-root * { box-sizing: border-box; }
          .fet-bento-root {
            position: relative;
            min-height: 100vh;
            padding: ${isSmallMobile ? '20px 14px 32px' : isMobile ? '24px 16px 36px' : '32px 52px 56px'};
            display: flex; flex-direction: column;
            overflow-x: hidden;
          }
          .fet-dot-bg {
            position: absolute; inset: 0;
            opacity: .35; pointer-events: none;
            background-image: radial-gradient(circle, #B5ACD8 1px, transparent 1px);
            background-size: 22px 22px;
            -webkit-mask-image: radial-gradient(ellipse 80% 70% at 50% 40%, black 30%, transparent 80%);
                    mask-image: radial-gradient(ellipse 80% 70% at 50% 40%, black 30%, transparent 80%);
          }
          .fet-bento-shell {
            position: relative; z-index: 1;
            max-width: 1280px; margin: 0 auto; width: 100%;
            flex: 1; display: flex; flex-direction: column;
          }
          .fet-bento-header {
            display: flex; justify-content: space-between; align-items: flex-start;
            gap: 16px; margin-bottom: ${isMobile ? '12px' : '20px'};
            padding-right: ${isMobile ? '92px' : '440px'};
          }
          .fet-header-text { display: flex; flex-direction: column; align-items: flex-start; gap: 10px; min-width: 0; }
          .fet-header-eyebrow {
            display: inline-flex; align-items: center; gap: 8px;
            font-size: 10px; font-weight: 800; letter-spacing: 2.4px;
            color: #5B52F0; text-transform: uppercase; margin-bottom: 6px;
          }
          .fet-header-eyebrow::before {
            content: ''; width: 6px; height: 6px; border-radius: 50%; background: #5B52F0;
            box-shadow: 0 0 0 4px rgba(91,82,240,.18);
          }
          .fet-header-title {
            font-size: ${isMobile ? '18px' : '22px'};
            font-weight: 700; letter-spacing: -.4px; color: #15142A;
          }
          .fet-streak-pill {
            display: inline-flex; align-items: center; gap: 9px;
            padding: 9px 15px; background: rgba(255,255,255,.85);
            -webkit-backdrop-filter: blur(12px); backdrop-filter: blur(12px);
            border-radius: 14px; border: 1px solid rgba(226,223,239,.85);
            font-size: 12px; color: #3A3950; font-weight: 600;
            box-shadow: 0 4px 14px rgba(21,20,42,.06);
          }
          .fet-streak-dot {
            width: 8px; height: 8px; border-radius: 50%; background: #10B981;
            box-shadow: 0 0 0 4px rgba(16,185,129,.18);
            animation: fet-streak-pulse 2.4s ease-in-out infinite;
          }
          @keyframes fet-streak-pulse {
            0%, 100% { box-shadow: 0 0 0 4px rgba(16,185,129,.18); }
            50% { box-shadow: 0 0 0 7px rgba(16,185,129,.05); }
          }
          .fet-hero-row {
            display: grid;
            grid-template-columns: ${isMobile ? '1fr' : '1fr 1.05fr'};
            gap: ${isSmallMobile ? '24px' : isMobile ? '28px' : '60px'};
            align-items: center;
            padding: ${isMobile ? '8px 0 24px' : '32px 0 56px'};
            position: relative;
          }
          .fet-hero-row::before {
            content: ''; position: absolute; inset: ${isMobile ? '0' : '-40px'};
            background: radial-gradient(ellipse 60% 60% at 70% 50%, rgba(91,82,240,.12), transparent 70%);
            pointer-events: none; z-index: 0;
          }
          .fet-hero-left { position: relative; z-index: 1; display: flex; flex-direction: column; }
          .fet-hero-pill {
            display: inline-flex; align-items: center; gap: 9px;
            align-self: flex-start;
            padding: 7px 15px; border-radius: 999px;
            background: #fff; border: 1px solid #E6E3F4;
            font-size: 12px; font-weight: 600; color: #6B6B7D;
            box-shadow: 0 1px 4px rgba(21,20,42,.06);
            margin-bottom: 22px;
          }
          .fet-hero-pill-dot {
            width: 7px; height: 7px; border-radius: 50%; background: #5B52F0;
            box-shadow: 0 0 0 4px rgba(91,82,240,.16);
          }
          .fet-hero-h1 {
            font-size: ${isSmallMobile ? '30px' : isMobile ? '36px' : '52px'};
            font-weight: 900; line-height: 1.08;
            letter-spacing: ${isSmallMobile ? '-1px' : '-1.5px'}; margin: 0 0 ${isMobile ? '14px' : '18px'};
            color: #15142A;
          }
          .fet-hero-h1 em {
            font-style: normal;
            background: linear-gradient(135deg, #4338CA 0%, #7C3AED 100%);
            -webkit-background-clip: text; -webkit-text-fill-color: transparent;
            background-clip: text;
          }
          .fet-hero-desc {
            font-size: ${isMobile ? '15px' : '17px'};
            color: #3A3950; line-height: 1.7;
            margin: 0 0 ${isMobile ? '24px' : '32px'}; max-width: 480px;
          }
          .fet-hero-ctas {
            display: flex; gap: 10px; flex-wrap: wrap;
            margin-bottom: ${isMobile ? '28px' : '38px'};
          }
          .fet-cta-primary, .fet-cta-secondary {
            ${isMobile ? 'flex: 1 1 140px; justify-content: center;' : ''}
          }
          .fet-cta-primary {
            padding: ${isSmallMobile ? '13px 20px' : '14px 28px'}; border-radius: 12px;
            font-size: ${isSmallMobile ? '14px' : '15px'}; font-weight: 700;
            border: none; background: #1A183C; color: #fff;
            cursor: pointer; display: inline-flex; align-items: center; gap: 9px;
            box-shadow: 0 8px 24px rgba(26,24,60,.26);
            transition: transform .15s ease, box-shadow .15s ease, background .15s ease;
            font-family: inherit;
          }
          .fet-cta-primary:hover {
            transform: translateY(-2px);
            background: #2D2A6E;
            box-shadow: 0 14px 34px rgba(26,24,60,.34);
          }
          .fet-cta-secondary {
            padding: ${isSmallMobile ? '13px 20px' : '14px 26px'}; border-radius: 12px;
            font-size: ${isSmallMobile ? '14px' : '15px'}; font-weight: 600;
            border: 1px solid #E6E3F4;
            background: rgba(255,255,255,.85);
            -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px);
            color: #3A3950; cursor: pointer;
            transition: all .15s ease; font-family: inherit;
            display: inline-flex; align-items: center;
          }
          .fet-cta-secondary:hover {
            border-color: #C4BEFF; color: #15142A; background: #fff;
          }
          .fet-hero-stats {
            display: grid; grid-template-columns: repeat(3, 1fr);
            border-radius: 18px; overflow: hidden;
            border: 1px solid #E6E3F4; background: #E6E3F4; gap: 1px;
            box-shadow: 0 4px 16px rgba(21,20,42,.06);
          }
          .fet-hero-stat {
            background: #fff;
            padding: ${isSmallMobile ? '14px 8px' : '18px 14px'};
            text-align: center;
            min-width: 0;
          }
          .fet-hero-stat-num {
            font-size: ${isSmallMobile ? '20px' : isMobile ? '22px' : '26px'};
            font-weight: 800; letter-spacing: -.5px;
            background: linear-gradient(135deg, #15142A 0%, #4338CA 100%);
            -webkit-background-clip: text; -webkit-text-fill-color: transparent;
            background-clip: text; color: transparent;
            font-variant-numeric: tabular-nums;
          }
          .fet-hero-stat-lbl {
            font-size: ${isSmallMobile ? '10px' : '11px'};
            color: #9896B0;
            margin-top: 4px; font-weight: 600;
          }
          .fet-stats-row {
            display: grid;
            grid-template-columns: ${isSmallMobile ? '1fr' : isMobile ? 'repeat(2, 1fr)' : '1.3fr 1fr 1fr 1fr'};
            gap: ${isMobile ? '10px' : '14px'};
            position: relative; z-index: 1;
          }
          .fet-hero-card {
            background:
              radial-gradient(ellipse at top right, rgba(147,134,255,.42) 0%, transparent 55%),
              radial-gradient(ellipse at bottom left, rgba(80,72,200,.32) 0%, transparent 60%),
              linear-gradient(148deg, #1A183C 0%, #2E2B72 55%, #4338CA 100%);
            border-radius: 24px;
            padding: ${isSmallMobile ? '24px 20px' : isMobile ? '26px 22px' : '32px 30px'};
            color: #fff;
            min-height: ${isSmallMobile ? '340px' : isMobile ? '380px' : '440px'};
            position: relative; overflow: hidden; z-index: 1;
            box-shadow:
              0 24px 60px -18px rgba(26,24,60,.55),
              0 4px 16px -4px rgba(91,82,240,.4),
              inset 0 1px 0 rgba(255,255,255,.1);
            display: flex; flex-direction: column;
          }
          .fet-hero-card::before {
            content: ''; position: absolute; inset: 0; pointer-events: none;
            background-image:
              linear-gradient(rgba(255,255,255,.04) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,.04) 1px, transparent 1px);
            background-size: 40px 40px;
            -webkit-mask-image: linear-gradient(180deg, black 0%, transparent 75%);
                    mask-image: linear-gradient(180deg, black 0%, transparent 75%);
          }
          .fet-hero-glow1, .fet-hero-glow2 { position: absolute; border-radius: 50%; pointer-events: none; filter: blur(8px); }
          .fet-hero-glow1 {
            top: -120px; right: -100px; width: 320px; height: 320px;
            background: radial-gradient(circle, rgba(147,134,255,.42) 0%, transparent 70%);
          }
          .fet-hero-glow2 {
            bottom: -80px; left: -60px; width: 240px; height: 240px;
            background: radial-gradient(circle, rgba(80,72,200,.32) 0%, transparent 70%);
          }
          .fet-hero-inner { position: relative; z-index: 1; flex: 1; display: flex; flex-direction: column; }
          .fet-hero-topbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
          .fet-exam-badge {
            display: inline-flex; align-items: center; gap: 7px; padding: 6px 12px;
            background: rgba(255,255,255,.1);
            -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px);
            border-radius: 999px;
            font-size: 10px; font-weight: 700; letter-spacing: 1.4px; text-transform: uppercase;
            border: 1px solid rgba(255,255,255,.16);
          }
          .fet-badge-dot {
            width: 7px; height: 7px; border-radius: 50%; background: #FBBF24;
            box-shadow: 0 0 8px rgba(251,191,36,.7);
          }
          .fet-hero-date {
            font-size: 10px; color: rgba(255,255,255,.55); letter-spacing: .8px;
            text-align: right; font-variant-numeric: tabular-nums;
          }
          .fet-countdown-eyebrow {
            margin-top: ${isMobile ? '20px' : '28px'};
            font-size: 10px; font-weight: 800; letter-spacing: 2px;
            color: rgba(255,255,255,.5); text-transform: uppercase;
          }
          .fet-countdown-block { flex: 1; display: flex; flex-direction: column; justify-content: center; }
          .fet-countdown-main {
            display: flex; align-items: baseline;
            gap: ${isSmallMobile ? '12px' : '18px'};
            font-variant-numeric: tabular-nums;
            flex-wrap: wrap;
          }
          .fet-countdown-days {
            font-size: ${isSmallMobile ? '72px' : isMobile ? '88px' : '128px'};
            font-weight: 800;
            letter-spacing: ${isSmallMobile ? '-5px' : '-7px'};
            line-height: .85;
            background: linear-gradient(180deg, #fff 0%, #ADA6FF 85%);
            -webkit-background-clip: text; -webkit-text-fill-color: transparent;
            background-clip: text; color: transparent;
            filter: drop-shadow(0 4px 24px rgba(173,166,255,.28));
          }
          .fet-countdown-label {
            font-size: 11px; font-weight: 700; letter-spacing: 1.6px;
            text-transform: uppercase; color: rgba(255,255,255,.7);
          }
          .fet-countdown-hms {
            font-size: 12px; color: rgba(255,255,255,.5);
            margin-top: 6px; font-variant-numeric: tabular-nums; letter-spacing: .6px;
          }
          .fet-on-track {
            margin-top: 18px; padding: 14px 16px;
            background: rgba(255,255,255,.07);
            -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px);
            border: 1px solid rgba(255,255,255,.1); border-radius: 14px;
            display: flex; align-items: center; gap: 12px;
          }
          .fet-on-track-icon {
            width: 38px; height: 38px; border-radius: 11px; flex-shrink: 0;
            background: linear-gradient(135deg, rgba(251,191,36,.22) 0%, rgba(251,191,36,.08) 100%);
            border: 1px solid rgba(251,191,36,.22);
            display: flex; align-items: center; justify-content: center;
          }
          .fet-on-track-label { font-size: 13px; font-weight: 700; color: #fff; }
          .fet-on-track-sub { font-size: 11px; color: rgba(255,255,255,.55); margin-top: 2px; line-height: 1.45; }

          .fet-prep-card, .fet-mini-card {
            background: linear-gradient(180deg, #FFFFFF 0%, #FCFAFF 100%);
            border-radius: 20px;
            border: 1px solid rgba(230,227,244,.85);
            box-shadow:
              0 1px 2px rgba(21,20,42,.04),
              0 8px 24px -10px rgba(21,20,42,.08);
          }
          .fet-prep-card {
            padding: ${isMobile ? '20px' : '22px'};
            display: flex; flex-direction: column;
            ${isMobile && !isSmallMobile ? 'grid-column: 1 / -1;' : ''}
          }
          .fet-prep-header {
            display: flex; justify-content: space-between; align-items: center;
            margin-bottom: 18px; gap: 10px;
          }
          .fet-prep-title { font-size: 13px; font-weight: 700; color: #15142A; letter-spacing: -.2px; }
          .fet-prep-pct {
            font-size: 11px; font-weight: 700; color: #5B52F0;
            background: linear-gradient(135deg, rgba(91,82,240,.12) 0%, rgba(91,82,240,.06) 100%);
            border: 1px solid rgba(91,82,240,.2);
            padding: 5px 10px; border-radius: 8px;
          }
          .fet-prep-bars { display: flex; flex-direction: column; gap: 14px; }
          .fet-bar-row { margin: 0; }
          .fet-bar-label-row {
            display: flex; justify-content: space-between; align-items: center;
            margin-bottom: 6px; gap: 10px;
          }
          .fet-bar-name { font-size: 12px; color: #3A3950; font-weight: 600; }
          .fet-bar-val { font-size: 11px; color: #9896B0; font-variant-numeric: tabular-nums; font-weight: 600; }
          .fet-bar-track {
            height: 8px; background: #F1EEF8; border-radius: 999px;
            overflow: hidden; position: relative;
          }
          .fet-bar-fill {
            height: 100%; border-radius: 999px;
            transition: width .8s cubic-bezier(.2,.6,.2,1);
            position: relative; overflow: hidden;
          }
          .fet-bar-fill::after {
            content: ''; position: absolute; inset: 0;
            background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,.4) 50%, transparent 100%);
            animation: fet-bar-shine 3.2s ease-in-out infinite;
          }
          @keyframes fet-bar-shine {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(220%); }
          }
          .fet-focus-btn {
            width: 100%; margin-top: 18px; padding: 11px 14px;
            border-radius: 12px;
            border: 1px solid #E6E3F4;
            background: linear-gradient(180deg, #FBFAFF 0%, #F4F2FB 100%);
            color: #3A3950;
            font-size: 12px; font-weight: 700; cursor: pointer;
            transition: all .15s ease; font-family: inherit;
          }
          .fet-focus-btn:hover {
            border-color: #C4BEFF;
            background: linear-gradient(180deg, #F3F0FD 0%, #EAE5FA 100%);
            color: #5B52F0;
            transform: translateY(-1px);
          }
          .fet-mini-card {
            padding: ${isSmallMobile ? '18px' : isMobile ? '20px' : '22px'};
            cursor: pointer;
            transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease;
            position: relative; overflow: hidden;
            display: flex; flex-direction: column;
            min-height: ${isSmallMobile ? '150px' : isMobile ? '160px' : '200px'};
          }
          .fet-mini-card:hover {
            transform: translateY(-4px);
            box-shadow:
              0 1px 2px rgba(21,20,42,.04),
              0 18px 36px -12px rgba(21,20,42,.16);
          }
          .fet-mini-topbar {
            display: flex; justify-content: space-between; align-items: flex-start; gap: 10px;
          }
          .fet-mini-icon {
            width: 38px; height: 38px; border-radius: 11px;
            display: flex; align-items: center; justify-content: center;
          }
          .fet-mini-days-badge {
            font-size: 10px; font-weight: 700; letter-spacing: .8px; text-transform: uppercase;
            padding: 4px 9px; border-radius: 7px;
          }
          .fet-mini-name {
            font-size: 14px; font-weight: 700; color: #15142A;
            margin-top: 14px; letter-spacing: -.3px; line-height: 1.25;
          }
          .fet-mini-date {
            font-size: 11px; color: #6B6B7D;
            margin-top: 4px; font-variant-numeric: tabular-nums;
          }
          .fet-mini-hint {
            font-size: 11px; color: #3A3950;
            margin-top: 14px; padding: 6px 10px;
            background: #FBFAFD; border: 1px solid #EEECF7; border-radius: 7px;
            display: inline-flex; align-items: center; gap: 6px; font-weight: 600;
            align-self: flex-start;
          }
        `}</style>

        <div style={{ position: 'fixed', top: '18px', right: '18px', zIndex: 60, display: 'flex', alignItems: 'center', gap: '10px' }}>
          {accountControl}
        </div>

        <div className="fet-bento-root">
          <div className="fet-dot-bg" />
          <div className="fet-bento-shell">
            <div className="fet-bento-header">
              <div className="fet-header-text">
                <div className="fet-header-eyebrow">Command Deck</div>
                <div className="fet-header-title">What&apos;s coming up</div>
                <div className="fet-streak-pill">
                  <div className="fet-streak-dot" />
                  {dashboardStats.loading
                    ? 'Loading streak…'
                    : dashboardStats.streakDays > 0
                      ? `Practice streak: ${dashboardStats.streakDays} day${dashboardStats.streakDays === 1 ? '' : 's'}`
                      : 'No streak yet — practise today to start one'}
                </div>
              </div>
            </div>

            <div className="fet-hero-row">
              <div className="fet-hero-left">
                <div className="fet-hero-pill">
                  <span className="fet-hero-pill-dot" />
                  {heroBadge}
                </div>
                <h1 className="fet-hero-h1">
                  {heroTitle.split('\n').map((line, idx) => (
                    <React.Fragment key={idx}>
                      {idx > 0 ? <br /> : null}
                      {line}
                    </React.Fragment>
                  ))}
                </h1>
                <p className="fet-hero-desc">{heroSubtitle}</p>
                <div className="fet-hero-ctas">
                  <button className="fet-cta-primary" type="button" onClick={openExamPicker}>
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="currentColor"><path d="M3 2l9 5-9 5z" /></svg>
                    {primaryCta}
                  </button>
                  <button className="fet-cta-secondary" type="button" onClick={() => navigate(pricingPath)}>
                    {secondaryCta}
                  </button>
                </div>
                <div className="fet-hero-stats">
                  <div className="fet-hero-stat" title={countdownActive ? `${countdown.days} day${countdown.days === 1 ? '' : 's'} until your FET exam (${countdownDateLabel})` : 'No FET exam date set yet'}>
                    <div className="fet-hero-stat-num">{countdownActive ? countdown.days : '—'}</div>
                    <div className="fet-hero-stat-lbl">{countdown.days === 1 ? 'Day to FET exam' : 'Days to FET exam'}</div>
                  </div>
                  <div className="fet-hero-stat">
                    <div className="fet-hero-stat-num">
                      {dashboardStats.loading ? '—' : dashboardStats.streakDays}
                    </div>
                    <div className="fet-hero-stat-lbl">
                      Day streak{dashboardStats.streakDays === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div className="fet-hero-stat">
                    <div className="fet-hero-stat-num">
                      {dashboardStats.loading || dashboardStats.overallReadinessPct === null
                        ? '—'
                        : `${dashboardStats.overallReadinessPct}%`}
                    </div>
                    <div className="fet-hero-stat-lbl">Ready</div>
                  </div>
                </div>
              </div>

              <div className="fet-hero-card">
                <div className="fet-hero-glow1" />
                <div className="fet-hero-glow2" />
                <div className="fet-hero-inner">
                  <div className="fet-hero-topbar">
                    <div className="fet-exam-badge">
                      <div className="fet-badge-dot" />
                      Exam Window
                    </div>
                    <div className="fet-hero-date">{countdownDateLabel}</div>
                  </div>
                  <div className="fet-countdown-eyebrow">Time to your FET exam</div>
                  <div className="fet-countdown-block">
                    <div className="fet-countdown-main">
                      <div className="fet-countdown-days">{countdownActive ? countdown.days : '—'}</div>
                      <div>
                        <div className="fet-countdown-label">{countdown.days === 1 ? 'day to go' : 'days to go'}</div>
                        <div className="fet-countdown-hms">
                          {countdownActive
                            ? `${String(countdown.hours).padStart(2, '0')}h : ${String(countdown.minutes).padStart(2, '0')}m : ${String(countdown.seconds).padStart(2, '0')}s until exam`
                            : 'Waiting for FET exam date'}
                        </div>
                      </div>
                    </div>
                    <div className="fet-on-track">
                      <div className="fet-on-track-icon">
                        <svg width="15" height="15" viewBox="0 0 16 16" fill="#FBBF24">
                          <path d="M8 1L10 6l5 .5-4 3.5 1 5-4-2.5L4 15l1-5-4-3.5L6 6z" />
                        </svg>
                      </div>
                      <div>
                        <div className="fet-on-track-label">You&apos;re on track</div>
                        <div className="fet-on-track-sub">
                          {countdownActive ? '45 min/day keeps you at target by exam day' : heroSubtitle}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="fet-stats-row">
              <div className="fet-prep-card">
                <div className="fet-prep-header">
                  <div className="fet-prep-title">Exam Readiness</div>
                  <div className="fet-prep-pct">
                    {dashboardStats.loading
                      ? '…'
                      : dashboardStats.overallReadinessPct === null
                        ? 'No data yet'
                        : `${dashboardStats.overallReadinessPct}% ready`}
                  </div>
                </div>
                <div className="fet-prep-bars">
                  {[
                    { name: 'Writing', pct: dashboardStats.writingPct, pending: dashboardStats.writingPending, gradient: 'linear-gradient(90deg, #6366F1 0%, #8B7CFF 100%)', comingSoon: false },
                    { name: 'Reading', pct: dashboardStats.readingPct, pending: dashboardStats.readingPending, gradient: 'linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)', comingSoon: false },
                  ].map((skill) => (
                    <div key={skill.name} className="fet-bar-row">
                      <div className="fet-bar-label-row">
                        <span className="fet-bar-name">
                          {skill.name}
                          {skill.comingSoon && (
                            <span style={{
                              marginLeft: 8,
                              padding: '2px 7px',
                              borderRadius: 6,
                              background: '#F1EFFE',
                              color: '#5B52F0',
                              fontSize: 10,
                              fontWeight: 800,
                              letterSpacing: '.05em',
                              textTransform: 'uppercase',
                              verticalAlign: 'middle',
                            }}>
                              Soon
                            </span>
                          )}
                        </span>
                        <span className="fet-bar-val">
                          {skill.comingSoon
                            ? 'Coming soon'
                            : dashboardStats.loading
                              ? '…'
                              : skill.pct !== null
                                ? `${skill.pct}%`
                                : skill.pending
                                  ? 'Marking…'
                                  : 'Not yet'}
                        </span>
                      </div>
                      <div className="fet-bar-track">
                        <div
                          className="fet-bar-fill"
                          style={{
                            width: `${skill.comingSoon || dashboardStats.loading || skill.pct === null ? 0 : skill.pct}%`,
                            background: skill.gradient,
                            opacity: skill.comingSoon ? 0.4 : 1,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  className="fet-focus-btn"
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  title="Open the full dashboard"
                >
                  Open dashboard →
                </button>
              </div>

              {calendarLoading
                ? Array.from({ length: 3 }).map((_, idx) => (
                    <div key={`break-skel-${idx}`} className="fet-mini-card" style={{ opacity: .55 }}>
                      <div className="fet-mini-name" style={{ color: '#9CA3AF' }}>Loading…</div>
                    </div>
                  ))
                : calendarEvents.length === 0
                  ? (
                    <div className="fet-mini-card" style={{ gridColumn: '1 / -1', opacity: .85 }}>
                      <div className="fet-mini-name">No upcoming breaks</div>
                      <div className="fet-mini-date">Your admin can add events from the Django admin panel.</div>
                    </div>
                  )
                  : calendarEvents.slice(0, 3).map((event) => {
                      const palette = accentFor(event.accent);
                      const status = computeDayStatus(event.starts_at, event.ends_at);
                      const isAway = !!event.user_away;
                      return (
                        <div
                          key={event.id}
                          className="fet-mini-card"
                          role="button"
                          tabIndex={0}
                          onClick={() => setActiveBreakModal(event)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveBreakModal(event); } }}
                          style={{ cursor: 'pointer', position: 'relative' }}
                          title={`Open plan for ${event.name}`}
                        >
                          {isAway && (
                            <span style={{
                              position: 'absolute', top: 12, right: 12,
                              fontSize: 9, fontWeight: 800, letterSpacing: '.08em',
                              padding: '3px 7px', borderRadius: 6,
                              background: '#1A183C', color: '#fff', textTransform: 'uppercase',
                            }}>
                              Away
                            </span>
                          )}
                          <div className="fet-mini-topbar">
                            <div className="fet-mini-icon" style={{ background: palette.bg, color: palette.fg }}>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                <path d={ICON_PATHS.calendar} />
                              </svg>
                            </div>
                            <div className="fet-mini-days-badge" style={{ color: palette.fg, background: palette.bg }}>
                              {status.label || '—'}
                            </div>
                          </div>
                          <div className="fet-mini-name">{event.name}</div>
                          <div className="fet-mini-date">{formatEventDateRange(event.starts_at, event.ends_at)}</div>
                          <div className="fet-mini-hint">
                            <svg width="8" height="8" viewBox="0 0 10 10" fill={palette.fg}><circle cx="5" cy="5" r="5" /></svg>
                            {event.hint || recommendationFor(event)}
                          </div>
                        </div>
                      );
                    })}
            </div>

          {activeBreakModal && (() => {
            const ev = activeBreakModal;
            const palette = accentFor(ev.accent);
            const status = computeDayStatus(ev.starts_at, ev.ends_at);
            const handleAwayChange = async (away) => {
              setOptInSaving(true);
              try {
                const updated = await setCalendarEventAway(ev.id, away);
                setCalendarEvents((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
                setActiveBreakModal(updated);
                // Recompute streak with new away state
                const events = calendarEvents.map((e) => (e.id === updated.id ? updated : e));
                const attempts = await getMyFetAttempts().catch(() => []);
                setDashboardStats(computeDashboardStats(attempts || [], events));
                setToast(away
                  ? `You're marked as away during ${ev.name}. Practice is paused — your streak is safe.`
                  : `Welcome back. You can practise during ${ev.name} now.`);
              } catch (error) {
                console.error(error);
                setToast('Could not update your break preference. Try again.');
              } finally {
                setOptInSaving(false);
              }
            };
            return (
              <div
                role="dialog"
                aria-modal="true"
                style={{
                  position: 'fixed', inset: 0, zIndex: 80,
                  background: 'rgba(15,23,42,.55)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '24px',
                }}
                onClick={() => setActiveBreakModal(null)}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: '100%', maxWidth: '460px',
                    background: '#fff', borderRadius: '22px',
                    padding: isMobile ? '24px 20px' : '28px',
                    boxShadow: '0 30px 80px rgba(15,23,42,.30)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '6px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ width: 36, height: 36, borderRadius: 11, background: palette.bg, color: palette.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d={ICON_PATHS.calendar} /></svg>
                      </span>
                      <span style={{ fontSize: '11px', fontWeight: 800, color: palette.fg, textTransform: 'uppercase', letterSpacing: '.12em' }}>
                        {status.label || '—'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setActiveBreakModal(null)}
                      aria-label="Close"
                      style={{ width: 32, height: 32, borderRadius: 999, border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontSize: 18, color: '#6B7280' }}
                    >
                      ×
                    </button>
                  </div>
                  <h3 style={{ margin: '4px 0 6px', fontSize: '24px', fontWeight: 800, color: '#15142A', lineHeight: 1.2 }}>{ev.name}</h3>
                  <div style={{ fontSize: '13px', color: '#6B6B7D', fontVariantNumeric: 'tabular-nums', marginBottom: '14px' }}>
                    {formatEventDateRange(ev.starts_at, ev.ends_at)}
                  </div>

                  <div style={{
                    background: palette.bg, color: palette.fg,
                    borderRadius: 12, padding: '12px 14px', marginBottom: 16,
                    fontSize: 13, fontWeight: 700,
                  }}>
                    {recommendationFor(ev)}
                  </div>

                  {ev.description && (
                    <p style={{ margin: '0 0 18px', fontSize: '14px', color: '#3A3950', lineHeight: 1.7 }}>
                      {ev.description}
                    </p>
                  )}

                  <div style={{
                    border: '1px solid #E6E3F4', borderRadius: 14, padding: '14px',
                    background: '#FBFAFF', marginBottom: 16,
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#15142A', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.08em' }}>
                      Will you practise during this break?
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        disabled={optInSaving}
                        onClick={() => handleAwayChange(false)}
                        style={{
                          flex: 1, height: 42, borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: optInSaving ? 'progress' : 'pointer',
                          border: !ev.user_away ? '2px solid #4338CA' : '1px solid #E6E3F4',
                          background: !ev.user_away ? '#EEF2FF' : '#fff',
                          color: !ev.user_away ? '#4338CA' : '#3A3950',
                          fontFamily: 'inherit',
                        }}
                      >
                        I'll be practising
                      </button>
                      <button
                        type="button"
                        disabled={optInSaving}
                        onClick={() => handleAwayChange(true)}
                        style={{
                          flex: 1, height: 42, borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: optInSaving ? 'progress' : 'pointer',
                          border: ev.user_away ? '2px solid #1A183C' : '1px solid #E6E3F4',
                          background: ev.user_away ? '#1A183C' : '#fff',
                          color: ev.user_away ? '#fff' : '#3A3950',
                          fontFamily: 'inherit',
                        }}
                      >
                        I'll be away
                      </button>
                    </div>
                    <div style={{ fontSize: 12, color: '#6B6B7D', marginTop: 8, lineHeight: 1.5 }}>
                      {ev.user_away
                        ? 'Practice is paused. Your streak is preserved across these days.'
                        : 'Practice continues as normal. Your daily target stays the same.'}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setActiveBreakModal(null)}
                    style={{
                      width: '100%', height: 46, borderRadius: 12, border: 'none',
                      background: '#1A183C', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            );
          })()}

          {!routeExamId && currentView === 'landing' && englishInviteOpen && (
            <div
              style={{
                position: 'fixed',
                zIndex: 40,
                display: 'flex',
                inset: 0,
                alignItems: isMobile ? 'flex-end' : 'flex-start',
                justifyContent: 'center',
                padding: isMobile ? '0' : '92px 24px 24px',
                pointerEvents: 'none'
              }}
            >
              <div
                style={{
                  width: '100%',
                  maxWidth: isMobile ? '100%' : '380px',
                  background: theme.surface,
                  border: `1px solid ${theme.border}`,
                  borderRadius: isMobile ? '26px 26px 0 0' : '28px',
                  boxShadow: '0 26px 80px rgba(15,23,42,.24)',
                  padding: isMobile ? '24px 18px 20px' : '28px',
                  marginTop: isMobile ? 'auto' : 0,
                  pointerEvents: 'auto'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', marginBottom: '14px' }}>
                  <div>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '7px 12px',
                      borderRadius: 999,
                      background: theme.accentSoftBg,
                      color: theme.accent,
                      fontSize: '12px',
                      fontWeight: 800,
                      marginBottom: '12px'
                    }}>
                      Intake Exam
                    </div>
                    <h3 style={{ margin: 0, fontSize: isMobile ? '24px' : '30px', fontWeight: 800, color: theme.text, lineHeight: 1.2 }}>
                      Try The Intake Exam
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={dismissEnglishInvite}
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 999,
                      border: `1px solid ${theme.border}`,
                      background: theme.surfaceSoft,
                      color: theme.body,
                      fontSize: '20px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  >
                    ×
                  </button>
                </div>

                <p style={{ margin: '0 0 18px', color: theme.body, fontSize: '15px', lineHeight: 1.8 }}>
                  Help your friends prepare to pass the college intake exam — share this link with them.
                </p>

                <a
                  href="https://examhub.xyz/"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    textDecoration: 'none',
                    padding: '14px 16px',
                    borderRadius: '16px',
                    border: `1px solid ${theme.border}`,
                    background: theme.surfaceSoft,
                    color: theme.text,
                    marginBottom: '18px',
                    wordBreak: 'break-all'
                  }}
                >
                  <span style={{ fontSize: '13px', fontWeight: 700 }}>https://examhub.xyz/</span>
                  <span style={{ fontSize: '18px', flexShrink: 0 }}>↗</span>
                </a>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <a
                    href="https://examhub.xyz/"
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      flex: isMobile ? '1 1 100%' : '0 0 auto',
                      minWidth: isMobile ? '100%' : '220px',
                      height: '48px',
                      borderRadius: '14px',
                      background: theme.ctaBg,
                      color: theme.ctaText,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textDecoration: 'none',
                      fontWeight: 800,
                      fontSize: '15px'
                    }}
                  >
                    Open
                  </a>
                  <button
                    type="button"
                    onClick={shareEnglishApp}
                    style={{
                      flex: isMobile ? '1 1 100%' : '0 0 auto',
                      minWidth: isMobile ? '100%' : '140px',
                      height: '48px',
                      borderRadius: '14px',
                      border: `1px solid ${theme.border}`,
                      background: theme.surfaceSoft,
                      color: theme.text,
                      fontWeight: 800,
                      fontSize: '15px',
                      cursor: 'pointer'
                    }}
                  >
                    Share
                  </button>
                  <button
                    type="button"
                    onClick={dismissEnglishInvite}
                    style={{
                      flex: isMobile ? '1 1 100%' : '0 0 auto',
                      minWidth: isMobile ? '100%' : '140px',
                      height: '48px',
                      borderRadius: '14px',
                      border: `1px solid ${theme.border}`,
                      background: theme.surface,
                      color: theme.body,
                      fontWeight: 800,
                      fontSize: '15px',
                      cursor: 'pointer'
                    }}
                  >
                    Maybe Later
                  </button>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'section-select') {
    // The section-select page has been retired — students pick exams from
    // the dashboard now. Use React Router's <Navigate> component (replace
    // mode) so the redirect fires during commit, with no flash of empty
    // UI. Any old bookmark to /?mode=section-select lands on /dashboard.
    return <Navigate to="/dashboard" replace />;
  }

  if (currentView === 'select') {
    return (
      <div style={{
        minHeight: '100vh',
        background: `linear-gradient(180deg, ${theme.pageBgAlt} 0%, ${theme.pageBgSoft} 100%)`,
        fontFamily: "'Outfit', sans-serif",
        color: theme.text
      }}>
        <div style={{ maxWidth: '1120px', margin: '0 auto', padding: isMobile ? '22px 16px 34px' : '28px 22px 50px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
            <button
              type="button"
              onClick={() => navigate('/')}
              style={{
                height: '42px',
                borderRadius: '999px',
                border: `1px solid ${theme.border}`,
                background: theme.surface,
                color: theme.body,
                padding: '0 16px',
                fontWeight: 800,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span aria-hidden="true">⌂</span>
              <span>Home</span>
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {accountControl}
            </div>
          </div>

          <section style={{
            background: theme.surface,
            borderRadius: '30px',
            border: `1px solid ${theme.border}`,
            boxShadow: theme.shadow,
            padding: isMobile ? '24px 18px' : '34px 32px',
            marginBottom: '22px'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '8px 12px',
              borderRadius: 999,
              background: theme.accentSoftBg,
              color: theme.accent,
              fontSize: '12px',
              fontWeight: 800,
              marginBottom: '14px'
            }}>
              Select Exam
            </div>
            <h1 style={{ margin: 0, fontSize: isMobile ? '30px' : '42px', fontWeight: 800, color: theme.text, lineHeight: 1.1 }}>
              Choose Your FET Writing Exam
            </h1>
            <p style={{ margin: '12px 0 0', fontSize: '15px', color: theme.body, lineHeight: 1.8, maxWidth: '760px' }}>
              Pick any available exam below. Each card opens that specific exam’s FET writing flow with the same login, attempt, and AI marking system.
            </p>
          </section>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px'
          }}>
            {availableExamsLoading ? (
              <div style={{
                background: theme.surfaceSoft,
                borderRadius: '20px',
                padding: '24px',
                border: `1px solid ${theme.border}`,
                color: theme.body,
                lineHeight: 1.8
              }}>
                Loading available FET exams...
              </div>
            ) : writingReadyExams.length > 0 ? writingReadyExams.map((availableExam) => (
              <div
                key={availableExam.id}
                style={{
                  background: theme.surface,
                  borderRadius: '24px',
                  padding: '24px',
                  boxShadow: theme.shadow,
                  border: `1px solid ${theme.border}`,
                  display: 'grid',
                  gap: '16px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '7px 12px',
                    borderRadius: 999,
                    background: theme.accentSoftBg,
                    color: theme.accent,
                    fontSize: '12px',
                    fontWeight: 800
                  }}>
                    FET Writing
                  </span>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '7px 12px',
                    borderRadius: 999,
                    background: theme.surfaceSoft,
                    color: theme.body,
                    fontSize: '12px',
                    fontWeight: 800
                  }}>
                    {availableExam.time_mins || 45} min
                  </span>
                </div>

                <div>
                  <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: theme.text, lineHeight: 1.3 }}>
                    {availableExam.title || 'FET Writing Exam'}
                  </h3>
                  <p style={{ margin: '10px 0 0', fontSize: '14px', color: theme.body, lineHeight: 1.75 }}>
                    {availableExam.description || 'Open this exam to begin the dedicated FET writing practice flow with AI marking.'}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => openPractice(availableExam.id)}
                  style={{
                    height: '48px',
                    borderRadius: '14px',
                    border: 'none',
                    background: theme.ctaBg,
                    color: theme.ctaText,
                    fontWeight: 800,
                    fontSize: '15px',
                    cursor: 'pointer'
                  }}
                >
                  Open This Exam
                </button>
              </div>
            )) : (
              <div style={{
                background: theme.surfaceSoft,
                borderRadius: '20px',
                padding: '24px',
                border: `1px solid ${theme.border}`,
                color: theme.body,
                lineHeight: 1.8
              }}>
                No FET writing exams are available right now.
              </div>
            )}
          </div>

          {!availableExamsLoading && examsTotal > 0 && (
            <div style={{
              marginTop: '24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '10px'
            }}>
              <div style={{ fontSize: '13px', color: theme.muted }}>
                Showing {availableExams.length} of {examsTotal} exam{examsTotal === 1 ? '' : 's'}
              </div>
              {examsHasMore && (
                <button
                  type="button"
                  onClick={loadMoreExams}
                  disabled={examsLoadingMore}
                  style={{
                    height: '44px',
                    padding: '0 22px',
                    borderRadius: '999px',
                    border: `1px solid ${theme.border}`,
                    background: theme.surface,
                    color: theme.text,
                    fontSize: '14px',
                    fontWeight: 800,
                    cursor: examsLoadingMore ? 'progress' : 'pointer',
                    opacity: examsLoadingMore ? 0.65 : 1,
                  }}
                >
                  {examsLoadingMore ? 'Loading…' : 'Load more exams'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Plan + credit gate for the writing practice flow. Direct entry to
  // /exam/<id>?mode=practice&section=writing skips the dashboard's
  // ExamStartConfirm modal, so we re-check here. Free-plan + 0 credits
  // means starting a writing attempt would 402 at createAttempt time.
  if (currentView === 'practice') {
    const plan = String(user?.plan || 'free').toLowerCase();
    const planAllowsWriting = user?.capabilities?.writing === true
      || (typeof user?.capabilities?.writing !== 'boolean' && (['promo', 'basic', 'ai'].includes(plan) || Number(user?.ai_credits || 0) > 0));
    const credits = Number(user?.ai_credits || 0);
    const canStartWriting = planAllowsWriting && credits >= 1;
    if (user && !canStartWriting) {
      const reason = !planAllowsWriting ? 'plan' : 'credits';
      const heading = reason === 'plan' ? 'Plan upgrade required' : 'Not enough AI credits';
      const body = reason === 'plan'
        ? "Writing isn't part of your current plan. Upgrade once and you'll unlock the writing exam with AI marking."
        : `Each writing attempt uses 1 AI credit for the AI marker. You currently have ${credits} ${credits === 1 ? 'credit' : 'credits'}. Top up before starting so your work can be marked.`;
      const ctaLabel = reason === 'plan' ? '🔒 View plans' : '🪙 Buy AI credits';
      const ctaHref = reason === 'plan' ? '/pricing?locked=writing&return_to=/dashboard' : '/pricing?locked=credits&return_to=/dashboard';
      return (
        <div style={{
          minHeight: '100vh', background: '#F8FAFC',
          display: 'grid', placeItems: 'center', padding: 24,
          fontFamily: "'DM Sans', system-ui, sans-serif",
        }}>
          <div style={{
            background: '#fff', borderRadius: 18,
            border: '1px solid #E2E8F0',
            maxWidth: 460, width: '100%', padding: 24,
            boxShadow: '0 18px 48px rgba(15,23,42,.08)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <span style={{
                width: 42, height: 42, borderRadius: 12,
                background: reason === 'plan' ? '#EEF2FF' : '#FEF3C7',
                color: reason === 'plan' ? '#5B52F0' : '#F59E0B',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, flexShrink: 0,
              }} aria-hidden="true">{reason === 'plan' ? '🔒' : '🪙'}</span>
              <div>
                <div style={{ fontSize: 11, color: '#64748B', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em' }}>
                  FET · Writing exam
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#0F172A', lineHeight: 1.25, marginTop: 2 }}>
                  {heading}
                </div>
              </div>
            </div>
            <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, marginBottom: 18 }}>{body}</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                style={{
                  height: 42, padding: '0 16px', borderRadius: 10,
                  border: '1px solid #E2E8F0', background: '#fff',
                  color: '#475569', fontWeight: 800, fontSize: 13,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >Back to dashboard</button>
              <button
                type="button"
                onClick={() => navigate(ctaHref)}
                style={{
                  height: 42, padding: '0 18px', borderRadius: 10,
                  border: 'none',
                  background: reason === 'plan' ? '#5B52F0' : '#F59E0B',
                  color: '#fff',
                  fontWeight: 800, fontSize: 13,
                  cursor: 'pointer', fontFamily: 'inherit',
                  letterSpacing: '.02em',
                }}
              >{ctaLabel}</button>
            </div>
          </div>
        </div>
      );
    }
  }

  // Plan gate at the FETExperience level — covers reading-onboarding,
  // reading-home, every reading-part-N, reading-exam, mistakes, vocab,
  // training. The individual screens (ReadingHome, ExamModeScreen) also
  // self-gate, but this short-circuits the whole reading section so a
  // free-plan student never sees the onboarding walkthrough either.
  const readingViewMatch = currentView && currentView.startsWith('reading-');
  if (readingViewMatch) {
    const readingAllowed = (() => {
      if (!user) return false;
      if (user.capabilities && typeof user.capabilities.reading === 'boolean') return user.capabilities.reading;
      return ['promo', 'basic', 'ai'].includes(String(user.effective_plan || user.plan || 'free').toLowerCase());
    })();
    if (!readingAllowed) {
      return (
        <div style={{
          minHeight: '100vh', background: '#F8FAFC',
          display: 'grid', placeItems: 'center', padding: 24,
          fontFamily: "'DM Sans', system-ui, sans-serif",
        }}>
          <div style={{
            background: '#fff', borderRadius: 18,
            border: '1px solid #E2E8F0',
            maxWidth: 460, width: '100%', padding: 24,
            boxShadow: '0 18px 48px rgba(15,23,42,.08)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <span style={{
                width: 42, height: 42, borderRadius: 12,
                background: '#EEF2FF', color: '#5B52F0',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, flexShrink: 0,
              }} aria-hidden="true">🔒</span>
              <div>
                <div style={{ fontSize: 11, color: '#64748B', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em' }}>
                  FET · Reading
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#0F172A', lineHeight: 1.25, marginTop: 2 }}>
                  Plan upgrade required
                </div>
              </div>
            </div>
            <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, marginBottom: 18 }}>
              Reading isn't part of your current plan. Upgrade once and you'll unlock every reading exam — practice and timed — on the platform.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                style={{
                  height: 42, padding: '0 16px', borderRadius: 10,
                  border: '1px solid #E2E8F0', background: '#fff',
                  color: '#475569', fontWeight: 800, fontSize: 13,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >Back to dashboard</button>
              <button
                type="button"
                onClick={() => navigate('/pricing?locked=reading&return_to=/dashboard')}
                style={{
                  height: 42, padding: '0 18px', borderRadius: 10,
                  border: 'none', background: '#5B52F0', color: '#fff',
                  fontWeight: 800, fontSize: 13,
                  cursor: 'pointer', fontFamily: 'inherit',
                  letterSpacing: '.02em',
                }}
              >🔒 View plans</button>
            </div>
          </div>
        </div>
      );
    }
  }

  if (currentView === 'reading-onboarding') {
    return <ReadingOnboarding theme={theme} isMobile={isMobile} navigate={navigate} />;
  }

  if (currentView === 'reading-home') {
    return (
      <ReadingHome
        theme={theme}
        isMobile={isMobile}
        isDark={isDark}
        navigate={navigate}
        toggleTheme={toggleTheme}
        accountControl={accountControl}
        exam={exam}
        loading={practiceLoading}
        setToast={setToast}
        onOpenExamMode={() => {
          // The student already saw the informational walkthrough when they
          // entered the reading flow — the Start-Exam confirmation modal
          // (raised by ExamModeScreen on first render) is enough here.
          const currentExamId = exam?.id || searchParams.get('examId') || '';
          const target = currentExamId
            ? `/?mode=reading-exam&examId=${encodeURIComponent(currentExamId)}`
            : '/?mode=reading-exam';
          setCurrentView('reading-exam');
          navigate(target);
        }}
        onOpenPart={(part) => {
          // CRITICAL: every reading-* URL must carry the examId or
          // resolveReadingExam() will fall back to the env-var default
          // (or the first exam with reading content in the catalogue),
          // so every "different" exam ends up showing the same content.
          const currentExamId = exam?.id || searchParams.get('examId') || resolvedExamId || '';
          const qp = currentExamId ? `&examId=${encodeURIComponent(currentExamId)}` : '';
          if (part.part_number === 1) {
            setCurrentView('reading-part-1');
            navigate(`/?mode=reading-part-1${qp}`);
            return;
          }
          if (part.part_number === 2) {
            setCurrentView('reading-part-2');
            navigate(`/?mode=reading-part-2${qp}`);
            return;
          }
          if (part.part_number === 3) {
            setCurrentView('reading-part-3');
            navigate(`/?mode=reading-part-3${qp}`);
            return;
          }
          if (part.part_number === 4) {
            setCurrentView('reading-part-4');
            navigate(`/?mode=reading-part-4${qp}`);
            return;
          }
          if (part.part_number === 5) {
            setCurrentView('reading-part-5');
            navigate(`/?mode=reading-part-5${qp}`);
            return;
          }
          setToast(`Part ${part.part_number} — coming in the next update.`);
        }}
      />
    );
  }

  // Compute "Continue to Part N+1" props for the current Part screen, based
  // on which next part has admin-uploaded content. Returns {} when this is
  // the last part with content (the screen falls back to "Back to home").
  const buildNextPartProps = (currentPartNumber) => {
    const allParts = exam?.reading_parts || [];
    const sorted = allParts
      .filter((p) => p && p.has_content && Number.isFinite(p.part_number))
      .sort((a, b) => a.part_number - b.part_number);
    const next = sorted.find((p) => p.part_number > currentPartNumber);
    if (!next) return {};
    return {
      nextPartLabel: `Continue to Part ${next.part_number} →`,
      onContinueToNextPart: () => {
        const currentExamId = exam?.id || searchParams.get('examId') || resolvedExamId || '';
        const qp = currentExamId ? `&examId=${encodeURIComponent(currentExamId)}` : '';
        setCurrentView(`reading-part-${next.part_number}`);
        navigate(`/?mode=reading-part-${next.part_number}${qp}`);
      },
    };
  };

  if (currentView === 'reading-part-1') {
    return (
      <Part1Screen
        theme={theme}
        isMobile={isMobile}
        isDark={isDark}
        navigate={navigate}
        toggleTheme={toggleTheme}
        accountControl={accountControl}
        exam={exam}
        loading={practiceLoading}
        setToast={setToast}
        {...buildNextPartProps(1)}
      />
    );
  }

  if (currentView === 'reading-part-2') {
    return (
      <Part2Screen
        theme={theme}
        isMobile={isMobile}
        isDark={isDark}
        navigate={navigate}
        toggleTheme={toggleTheme}
        accountControl={accountControl}
        exam={exam}
        loading={practiceLoading}
        setToast={setToast}
        {...buildNextPartProps(2)}
      />
    );
  }

  if (currentView === 'reading-part-3') {
    return (
      <Part3Screen
        theme={theme}
        isMobile={isMobile}
        isDark={isDark}
        navigate={navigate}
        toggleTheme={toggleTheme}
        accountControl={accountControl}
        exam={exam}
        loading={practiceLoading}
        setToast={setToast}
        {...buildNextPartProps(3)}
      />
    );
  }

  if (currentView === 'reading-part-4') {
    return (
      <Part4Screen
        theme={theme}
        isMobile={isMobile}
        isDark={isDark}
        navigate={navigate}
        toggleTheme={toggleTheme}
        accountControl={accountControl}
        exam={exam}
        loading={practiceLoading}
        setToast={setToast}
        {...buildNextPartProps(4)}
      />
    );
  }

  if (currentView === 'reading-part-5') {
    return (
      <Part5Screen
        theme={theme}
        isMobile={isMobile}
        isDark={isDark}
        navigate={navigate}
        toggleTheme={toggleTheme}
        accountControl={accountControl}
        exam={exam}
        loading={practiceLoading}
        setToast={setToast}
        {...buildNextPartProps(5)}
      />
    );
  }


  if (currentView === 'reading-exam-picker') {
    return (
      <ReadingExamPicker
        theme={theme}
        isMobile={isMobile}
        isDark={isDark}
        navigate={navigate}
        toggleTheme={toggleTheme}
        accountControl={accountControl}
        exams={availableExams || []}
        loading={availableExamsLoading}
        setToast={setToast}
      />
    );
  }

  if (currentView === 'reading-exam') {
    return (
      <ExamModeScreen
        theme={theme}
        isMobile={isMobile}
        isDark={isDark}
        navigate={navigate}
        toggleTheme={toggleTheme}
        accountControl={accountControl}
        exam={exam}
        loading={practiceLoading}
        setToast={setToast}
      />
    );
  }

  if (currentView === 'reading-mistakes' || currentView === 'reading-vocabulary' || currentView === 'reading-training') {
    const view = currentView === 'reading-mistakes' ? 'mistakes'
      : currentView === 'reading-vocabulary' ? 'vocabulary'
      : 'training';
    return (
      <ReadingExtras
        theme={theme}
        isMobile={isMobile}
        isDark={isDark}
        navigate={navigate}
        toggleTheme={toggleTheme}
        accountControl={accountControl}
        exam={exam}
        view={view}
        setToast={setToast}
      />
    );
  }

  if (currentView === 'reading-select') {
    return (
      <div style={{
        minHeight: '100vh',
        background: `linear-gradient(180deg, ${theme.pageBgAlt} 0%, ${theme.pageBgSoft} 100%)`,
        fontFamily: "'Outfit', sans-serif",
        color: theme.text
      }}>
        <div style={{ maxWidth: '1120px', margin: '0 auto', padding: isMobile ? '22px 16px 34px' : '28px 22px 50px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
            <button
              type="button"
              onClick={() => navigate('/')}
              style={{
                height: '42px',
                borderRadius: '999px',
                border: `1px solid ${theme.border}`,
                background: theme.surface,
                color: theme.body,
                padding: '0 16px',
                fontWeight: 800,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span aria-hidden="true">⌂</span>
              <span>Home</span>
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {accountControl}
            </div>
          </div>

          <section style={{
            background: theme.surface,
            borderRadius: '30px',
            border: `1px solid ${theme.border}`,
            boxShadow: theme.shadow,
            padding: isMobile ? '24px 18px' : '34px 32px',
            marginBottom: '22px'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '8px 12px',
              borderRadius: 999,
              background: theme.accentSoftBg,
              color: theme.accent,
              fontSize: '12px',
              fontWeight: 800,
              marginBottom: '14px'
            }}>
              Select Reading Exam
            </div>
            <h1 style={{ margin: 0, fontSize: isMobile ? '30px' : '42px', fontWeight: 800, color: theme.text, lineHeight: 1.1 }}>
              Choose Your FET Reading Exam
            </h1>
            <p style={{ margin: '12px 0 0', fontSize: '15px', color: theme.body, lineHeight: 1.8, maxWidth: '760px' }}>
              Only exams with live reading content are shown here. Pick one to open the reading experience and test the full reading screens.
            </p>
          </section>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px'
          }}>
            {availableExamsLoading ? (
              <div style={{
                background: theme.surfaceSoft,
                borderRadius: '20px',
                padding: '24px',
                border: `1px solid ${theme.border}`,
                color: theme.body,
                lineHeight: 1.8
              }}>
                Loading available FET reading exams...
              </div>
            ) : readingReadyExams.length > 0 ? readingReadyExams.map((availableExam) => {
              const questionTotal = (availableExam.reading_parts || []).reduce((sum, part) => sum + (part.question_count || 0), 0);
              const readyParts = (availableExam.reading_parts || []).filter((part) => part.has_content && (part.question_count || 0) > 0).length;
              return (
                <div
                  key={availableExam.id}
                  style={{
                    background: theme.surface,
                    borderRadius: '24px',
                    padding: '24px',
                    boxShadow: theme.shadow,
                    border: `1px solid ${theme.border}`,
                    display: 'grid',
                    gap: '16px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '7px 12px',
                      borderRadius: 999,
                      background: theme.accentSoftBg,
                      color: theme.accent,
                      fontSize: '12px',
                      fontWeight: 800
                    }}>
                      FET Reading
                    </span>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '7px 12px',
                      borderRadius: 999,
                      background: theme.surfaceSoft,
                      color: theme.body,
                      fontSize: '12px',
                      fontWeight: 800
                    }}>
                      {readyParts} parts · {questionTotal} questions
                    </span>
                  </div>

                  <div>
                    <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: theme.text, lineHeight: 1.3 }}>
                      {availableExam.title || 'FET Reading Exam'}
                    </h3>
                    <p style={{ margin: '10px 0 0', fontSize: '14px', color: theme.body, lineHeight: 1.75 }}>
                      {availableExam.description || 'Open this exam to begin the dedicated FET reading flow.'}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => openReading(availableExam.id)}
                    style={{
                      height: '48px',
                      borderRadius: '14px',
                      border: 'none',
                      background: theme.ctaBg,
                      color: theme.ctaText,
                      fontWeight: 800,
                      fontSize: '15px',
                      cursor: 'pointer'
                    }}
                  >
                    Open Reading Exam
                  </button>
                </div>
              );
            }) : (
              <div style={{
                background: theme.surfaceSoft,
                borderRadius: '20px',
                padding: '24px',
                border: `1px solid ${theme.border}`,
                color: theme.body,
                lineHeight: 1.8
              }}>
                No FET reading exams are available right now.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'reading') {
    const readyParts = (exam?.reading_parts || []).filter((p) => p.has_content);
    const totalReadingQuestions = readyParts.reduce((sum, part) => sum + (part.question_count || 0), 0);

    return (
      <div style={{ minHeight: '100vh', background: `linear-gradient(180deg, ${theme.pageBgSoft} 0%, ${theme.pageBg} 100%)`, color: theme.text, fontFamily: "'Inter', 'Outfit', system-ui, sans-serif" }}>
        <div style={{ maxWidth: '1120px', margin: '0 auto', padding: isMobile ? '18px 12px 110px' : '34px 24px 120px' }}>
          <div className="fet-surface" style={{ borderRadius: '28px', padding: isMobile ? '22px 18px' : '28px 30px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 12px', borderRadius: 999, background: theme.accentSoftBg, color: theme.accent, fontSize: '12px', fontWeight: 800, marginBottom: '14px' }}>
                  FET Reading
                </div>
                <h1 style={{ margin: 0, fontSize: isMobile ? '28px' : '40px', fontWeight: 800, color: theme.text, lineHeight: 1.08 }}>
                  {exam?.title || 'Reading Practice'}
                </h1>
                <p style={{ margin: '12px 0 0', fontSize: '15px', color: theme.body, lineHeight: 1.8, maxWidth: '760px' }}>
                  Use the reading design direction as the live frontendFET reading experience while keeping the exam content connected to your backend.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={backToLanding} style={{ height: '44px', borderRadius: '14px', border: `1px solid ${theme.border}`, background: '#fff', color: theme.text, fontWeight: 700, padding: '0 18px', cursor: 'pointer' }}>Back</button>
                {!readingStarted && <button onClick={() => setReadingStarted(true)} style={{ height: '44px', borderRadius: '14px', border: 'none', background: theme.ctaBg, color: theme.ctaText, fontWeight: 800, padding: '0 18px', cursor: 'pointer' }}>Start Reading</button>}
              </div>
            </div>
          </div>

          {!practiceLoading && !readingStarted && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
              {readyParts.map((part) => (
                <div key={part.id} className="fet-surface" style={{ borderRadius: '22px', padding: '22px' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 12, background: theme.accent, color: '#fff', fontSize: 14, fontWeight: 800, marginBottom: 14 }}>
                    {part.part_number}
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: theme.text, marginBottom: 6 }}>{part.type_name}</div>
                  <div style={{ fontSize: 13, color: theme.body, lineHeight: 1.7, marginBottom: 14 }}>{part.description}</div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', padding: '7px 12px', borderRadius: 999, background: theme.accentSoftBg, color: theme.accent, fontSize: 12, fontWeight: 800 }}>
                    {part.question_count || 0} questions
                  </div>
                </div>
              ))}
              {!readyParts.length && (
                <div className="fet-surface" style={{ borderRadius: '22px', padding: '22px', color: theme.body }}>
                  No reading parts are available in this exam yet.
                </div>
              )}
            </div>
          )}

          {!practiceLoading && readingStarted && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 12px', borderRadius: 999, background: theme.surface, border: `1px solid ${theme.border}`, color: theme.text, fontSize: 12, fontWeight: 800 }}>{readyParts.length} parts</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 12px', borderRadius: 999, background: theme.surface, border: `1px solid ${theme.border}`, color: theme.text, fontSize: 12, fontWeight: 800 }}>{totalReadingQuestions} questions</span>
                </div>
                {!readingResult && (
                  <button onClick={handleSubmitReading} disabled={readingSubmitting} style={{ height: '44px', borderRadius: '14px', border: 'none', background: theme.ctaBg, color: theme.ctaText, fontWeight: 800, padding: '0 18px', cursor: readingSubmitting ? 'wait' : 'pointer', opacity: readingSubmitting ? 0.7 : 1 }}>
                    {readingSubmitting ? 'Scoring…' : 'Submit Reading'}
                  </button>
                )}
              </div>

              {readyParts.map((part) => renderReadingPart(part))}

              {readingResult && (
                <div className="fet-surface" style={{ borderRadius: '24px', padding: isMobile ? '22px 18px' : '28px 30px', marginTop: '18px' }}>
                  <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '.16em', color: theme.accent, fontWeight: 800, marginBottom: '8px' }}>
                    Reading Result
                  </div>
                  <div style={{ fontSize: isMobile ? '34px' : '46px', fontWeight: 900, color: theme.text, lineHeight: 1 }}>
                    {readingResult.total_score}<span style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: theme.body }}>/ {readingResult.max_score}</span>
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '14px', color: theme.body }}>{readingResult.percentage}% correct</div>
                  <div style={{ display: 'grid', gap: '10px', marginTop: '18px' }}>
                    {(readingResult.part_scores || []).map((ps) => (
                      <div key={ps.part_number} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '14px 16px', borderRadius: '16px', border: `1px solid ${theme.border}`, background: theme.surfaceAlt }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: theme.text }}>Part {ps.part_number}</div>
                        <div style={{ fontSize: '16px', fontWeight: 800, color: theme.accent }}>{ps.score}/{ps.max}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // Practice System - FET Exam Interface
  return (
    <div style={{
      minHeight: '100vh',
      background: theme.pageBg,
      fontFamily: "'Inter', 'Outfit', system-ui, sans-serif",
      color: theme.text
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Outfit:wght@400;500;600;700;800&display=swap');

        body.fet-text-small { --fet-readable-size: 14px; }
        body.fet-text-normal { --fet-readable-size: 16px; }
        body.fet-text-large { --fet-readable-size: 18px; }

        body.fet-spacing-compact { --fet-readable-line-height: 1.5; }
        body.fet-spacing-normal { --fet-readable-line-height: 1.75; }
        body.fet-spacing-relaxed { --fet-readable-line-height: 2.1; }

        body.fet-font-default { --fet-readable-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }
        body.fet-font-serif { --fet-readable-family: Georgia, "Times New Roman", serif; }
        body.fet-font-dyslexic { --fet-readable-family: "Open-Dyslexic", "OpenDyslexic", Arial, sans-serif; }

        .fet-readable-text,
        .fet-readable-list li,
        .fet-readable-box,
        .fet-readable-textarea {
          font-size: var(--fet-readable-size, 16px) !important;
          line-height: var(--fet-readable-line-height, 1.75) !important;
          font-family: var(--fet-readable-family, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif) !important;
          transition: font-size 150ms ease, line-height 150ms ease;
        }

        .fet-shell {
          position: relative;
          overflow-x: hidden;
        }

        .fet-surface {
          border: 1px solid ${theme.border};
          background: ${theme.surface};
          box-shadow: ${theme.shadow};
        }

        .fet-topbar-btn,
        .fet-action-btn,
        .fet-ghost-btn,
        .fet-nav-btn,
        .fet-chip-btn {
          transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease, border-color 0.18s ease;
        }

        .fet-topbar-btn:hover,
        .fet-action-btn:hover,
        .fet-ghost-btn:hover,
        .fet-nav-btn:hover,
        .fet-chip-btn:hover {
          transform: translateY(-1px);
        }

        .fet-action-btn:hover,
        .fet-nav-btn:hover {
          box-shadow: 0 10px 18px rgba(79, 70, 229, 0.14);
        }

        .fet-ghost-btn:hover {
          background: ${theme.surfaceSoft};
        }

        .fet-topic-card:hover {
          transform: translateY(-1px);
          box-shadow: ${theme.shadow};
        }

        .fet-textarea {
          transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
        }

        .fet-textarea:focus {
          outline: none;
          border-color: ${theme.accent};
          box-shadow: 0 0 0 3px ${isDark ? 'rgba(129,140,248,0.18)' : 'rgba(79,70,229,0.1)'};
          background: ${theme.surface};
        }

        .fet-textarea::placeholder {
          color: ${isDark ? '#64748B' : '#D1D5DB'};
          font-style: italic;
        }

        .fet-bullets {
          list-style: none;
          padding: 0;
          margin: 0;
          display: grid;
          gap: 12px;
        }

        .fet-bullets li {
          position: relative;
          padding-left: 22px;
          color: ${theme.body};
          line-height: 1.7;
        }

        .fet-bullets li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.72em;
          width: 6px;
          height: 6px;
          border-radius: 2px;
          background: ${theme.accent};
          transform: translateY(-50%);
        }

        .fet-timer-pulse {
          position: relative;
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: ${theme.accent};
        }

        .fet-timer-pulse::after {
          content: '';
          position: absolute;
          inset: -5px;
          border: 1px solid ${isDark ? 'rgba(129,140,248,0.45)' : 'rgba(79, 70, 229, 0.35)'};
          border-radius: 999px;
          animation: fetPulse 1.8s ease-out infinite;
        }

        @keyframes fetPulse {
          0% { transform: scale(0.9); opacity: 0.7; }
          70% { transform: scale(1.7); opacity: 0; }
          100% { transform: scale(1.7); opacity: 0; }
        }

        @keyframes fetSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .fet-assessment-name {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: ${isMobile ? '100%' : '340px'};
        }

        @media (max-width: 768px) {
          .fet-assessment-name {
            white-space: normal;
            max-width: 100%;
          }
          /* The fixed action bar at the bottom of the writing exam was
             overlapping the textarea and the prompt list on small phones —
             students couldn't see what they were typing. Push the page
             content up by enough to clear the bar (≈ 230px tall on mobile
             with progress + font + question-nav + Prev/Next + nudge), and
             give the bar a tighter padding so it doesn't dominate. */
          .fet-shell {
            padding-bottom: 230px !important;
          }
          .fet-surface[style*="position: fixed"][style*="bottom"] {
            padding: 10px 12px !important;
          }
          .fet-surface[style*="position: fixed"][style*="bottom"] .fet-nav-btn {
            padding: 10px 12px !important;
            font-size: 13px !important;
          }
          .fet-surface[style*="position: fixed"][style*="bottom"] .fet-action-btn {
            height: 40px !important;
            padding: 0 16px !important;
            font-size: 13px !important;
          }
          /* Hide the redundant "Question progress" / "AI result ready"
             label on mobile — the progress bar + N/M chip already
             communicate the same thing. */
          .fet-surface[style*="position: fixed"][style*="bottom"] .fet-progress-label {
            display: none !important;
          }
        }
      `}</style>
      <div className="fet-shell">
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 20,
            background: theme.surfaceAlt,
            borderBottom: `1px solid ${theme.border}`
          }}
        >
          <div
            style={{
              maxWidth: '1200px',
              margin: '0 auto',
              minHeight: '64px',
              padding: isMobile ? '10px 12px' : '10px 20px',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'minmax(320px, 1fr) minmax(220px, auto)',
                gap: isMobile ? '12px' : '18px',
                alignItems: 'center'
              }}
            >
              <div style={{ display: 'grid', gap: '10px', minWidth: 0 }}>
                <div>
                  <button
                    type="button"
                    onClick={backToLanding}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 14px',
                      borderRadius: '999px',
                      border: `1px solid ${theme.border}`,
                      background: theme.surfaceSoft,
                      color: theme.body,
                      fontSize: '12px',
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    <span aria-hidden="true">⌂</span>
                    <span>Home</span>
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                  <div
                    style={{
                      width: isMobile ? '40px' : '44px',
                      height: isMobile ? '40px' : '44px',
                      borderRadius: '14px',
                      background: isDark
                        ? 'linear-gradient(135deg, #6366F1 0%, #4338CA 100%)'
                        : 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
                      color: '#ffffff',
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: isMobile ? '14px' : '15px',
                      fontWeight: 900,
                      flex: '0 0 auto',
                      boxShadow: isDark ? '0 10px 24px rgba(79,70,229,.28)' : '0 10px 20px rgba(79,70,229,.16)',
                    }}
                  >
                    FET
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <h1
                      className="fet-assessment-name"
                      style={{
                        fontSize: isMobile ? '20px' : '24px',
                        fontWeight: 800,
                        color: theme.text,
                        margin: 0,
                        lineHeight: 1.1,
                        letterSpacing: '-0.02em',
                      }}
                    >
                      FET Writing Preparation
                    </h1>
                    <div style={{ fontSize: '12px', color: theme.muted, marginTop: '4px', fontWeight: 600 }}>
                      Timed writing practice with AI marking
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isMobile ? 'space-between' : 'flex-end',
                  gap: '12px',
                  flexWrap: 'wrap'
                }}
              >
                <div
                  style={{
                    minWidth: isMobile ? '100%' : '240px',
                    display: 'grid',
                    gap: '8px',
                    padding: isMobile ? '12px 14px' : '12px 16px',
                    borderRadius: '18px',
                    background: theme.surface,
                    border: `1px solid ${examTimerTone.border}`,
                    boxShadow: examTimerTone.shadow,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px', color: theme.muted, fontWeight: 900, letterSpacing: '.16em', textTransform: 'uppercase' }}>
                      {examTimerTone.label}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: examTimerTone.badgeText, background: examTimerTone.badgeBg, fontSize: '12px', fontWeight: 800, padding: '6px 10px', borderRadius: '999px' }}>
                      <span className="fet-timer-pulse" style={{ background: examTimerTone.badgeText, boxShadow: 'none', width: 7, height: 7 }} />
                      {examTimerTone.status}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: isMobile ? '24px' : '28px', fontWeight: 900, color: examTimerTone.timeColor, letterSpacing: '.06em', lineHeight: 1 }}>
                      {formatTime(timeLeft)}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {accountControl}
                </div>
              </div>
            </div>
          </div>
        </div>

        {toast && (
          <div
            style={{
              position: 'fixed',
              top: 86,
              right: 18,
              zIndex: 40,
              maxWidth: 360,
              background: theme.surface,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: 12,
              boxShadow: theme.shadow,
              padding: '12px 14px',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {toast}
          </div>
        )}

        {!practiceLoading && exam && (
          <>
            <div
              onClick={() => setNavDrawerOpen(false)}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.3)',
                opacity: navDrawerOpen ? 1 : 0,
                pointerEvents: navDrawerOpen ? 'auto' : 'none',
                transition: 'opacity 300ms ease',
                zIndex: 36,
              }}
            />
            <aside
              ref={navDrawerRef}
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: '320px',
                maxWidth: '100%',
                height: '100vh',
                background: '#ffffff',
                boxShadow: '-8px 0 32px rgba(0,0,0,0.08)',
                transform: navDrawerOpen ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 300ms ease',
                zIndex: 37,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #F3F4F6' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '14px' }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                      Question navigation
                    </h2>
                    <div style={{ marginTop: '6px', fontSize: '13px', color: '#9CA3AF' }}>
                      {answeredCount} of {totalNavQuestions} questions answered
                    </div>
                  </div>
                  <button
                    onClick={() => setNavDrawerOpen(false)}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '999px',
                      border: 'none',
                      background: 'transparent',
                      color: '#6B7280',
                      cursor: 'pointer',
                      fontSize: '16px',
                    }}
                  >
                    ✕
                  </button>
                </div>
                <div style={{ height: '4px', width: '100%', background: '#E5E7EB', borderRadius: '99px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${answeredPercent}%`, background: '#4F46E5', borderRadius: '99px' }} />
                </div>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px 120px' }}>
                {navSections.map((section) => (
                  <section key={section.key} style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '10px' }}>
                      <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: '#9CA3AF', fontWeight: 700 }}>
                        {section.label}
                      </div>
                      <span style={{ fontSize: '10px', color: '#9CA3AF', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '999px', padding: '4px 8px', fontWeight: 700 }}>
                        {section.badge}
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                      {section.items.map((item) => {
                        const isCurrent = item.current;
                        const isFlagged = item.flagged;
                        const isAnswered = item.answered;
                        const baseStyles = isCurrent
                          ? { background: '#4F46E5', color: '#fff', border: 'none' }
                          : isFlagged
                            ? { background: '#FFFBEB', color: '#92400E', border: '1px solid #FCD34D' }
                            : isAnswered
                              ? { background: '#EEF2FF', color: '#4F46E5', border: '1px solid #C7D2FE' }
                              : { background: '#F9FAFB', color: '#6B7280', border: '1px solid #E5E7EB' };

                        return (
                          <div
                            key={item.id}
                            onClick={() => navigateToNavItem(item)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                navigateToNavItem(item);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            style={{
                              position: 'relative',
                              height: '48px',
                              borderRadius: '10px',
                              fontSize: '14px',
                              fontWeight: 500,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              outline: 'none',
                              ...baseStyles,
                            }}
                          >
                            <span>{item.number}</span>
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleFlag(item.id);
                              }}
                              style={{
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                border: 'none',
                                background: 'transparent',
                                color: isFlagged ? '#F59E0B' : (isCurrent ? 'rgba(255,255,255,0.72)' : '#9CA3AF'),
                                fontSize: '10px',
                                cursor: 'pointer',
                                padding: 0,
                                lineHeight: 1,
                              }}
                            >
                              🚩
                            </button>
                            {isCurrent && (
                              <span
                                style={{
                                  position: 'absolute',
                                  bottom: '-8px',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  width: '6px',
                                  height: '6px',
                                  borderRadius: '999px',
                                  background: '#4F46E5',
                                }}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                ))}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '11px', color: '#9CA3AF' }}>
                  <span>⬜ Unanswered</span>
                  <span>🟦 Answered</span>
                  <span>🟨 Flagged</span>
                  <span>🟪 Current</span>
                </div>
              </div>

              <div style={{ position: 'sticky', bottom: 0, background: '#ffffff', borderTop: '1px solid #F3F4F6', padding: '16px 20px 20px' }}>
                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '12px' }}>
                  {answeredCount} answered · {flaggedCount} flagged · {skippedCount} skipped
                </div>
                <button
                  onClick={() => {
                    if (firstUnanswered) navigateToNavItem(firstUnanswered);
                  }}
                  style={{
                    width: '100%',
                    height: '44px',
                    borderRadius: '10px',
                    border: 'none',
                    background: '#4F46E5',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: firstUnanswered ? 'pointer' : 'not-allowed',
                    opacity: firstUnanswered ? 1 : 0.6,
                  }}
                >
                  Go to first unanswered
                </button>
              </div>
            </aside>
          </>
        )}

        <div
          style={{
            maxWidth: '1120px',
            margin: '0 auto',
            padding: isMobile ? '18px 12px 190px' : isTablet ? '28px 18px 168px' : '36px 24px 140px'
          }}
        >
          {!practiceLoading && exam && !result && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', marginBottom: '18px', flexWrap: 'wrap' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '44px',
                    height: '44px',
                    borderRadius: '999px',
                    background: theme.accent,
                    color: '#fff',
                    fontSize: '16px',
                    fontWeight: 800,
                    boxShadow: 'none'
                  }}
                >
                  {currentStep}
                </span>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '9px 14px',
                    borderRadius: '6px',
                    background: theme.surface,
                    border: `1px solid ${theme.accent}`,
                    color: theme.accent,
                    fontSize: '12px',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '.1em'
                  }}
                >
                  Writing - Part {currentPart}
                </span>
              </div>
            </div>
          )}

          {practiceLoading && (
            <div className="fet-surface" style={{ borderRadius: '16px', padding: '32px', textAlign: 'center', color: theme.body }}>
              Preparing your FET writing attempt...
            </div>
          )}

          {/* Restored-draft notice — appears when we re-loaded answers from
              localStorage so the user knows their work survived the page
              refresh / tab close. Auto-dismisses by clicking ✕. */}
          {!practiceLoading && draftRestoredAt && !attemptStartError && (
            <div style={{ maxWidth: 720, margin: '12px auto 0' }}>
              <div style={{
                background: '#EEF2FF',
                border: '1px solid #c7d2fe',
                borderRadius: 12,
                padding: '10px 14px',
                color: '#3730A3',
                fontSize: 13,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span aria-hidden="true">💾</span>
                <span style={{ flex: 1, fontWeight: 700 }}>
                  Restored your unsaved draft from {new Date(draftRestoredAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}.
                </span>
                <button
                  type="button"
                  onClick={() => setDraftRestoredAt(null)}
                  style={{ background: 'transparent', border: 'none', color: '#3730A3', cursor: 'pointer', fontSize: 16, padding: 0 }}
                  aria-label="Dismiss"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* Block-the-page banner when createAttempt failed (insufficient
              credits / plan locked / generic). Tells the user exactly what's
              wrong + a one-click way to resolve it. */}
          {!practiceLoading && attemptStartError && (
            <div style={{ maxWidth: 720, margin: '24px auto', padding: 0 }}>
              <div style={{
                background: attemptStartError.tone === 'credits' ? '#FFFBEB' : attemptStartError.tone === 'plan' ? '#EFF6FF' : '#FEF2F2',
                border: `1px solid ${attemptStartError.tone === 'credits' ? '#FBBF24' : attemptStartError.tone === 'plan' ? '#3B82F6' : '#F87171'}`,
                borderRadius: 16,
                padding: '22px 24px',
                color: attemptStartError.tone === 'credits' ? '#78350F' : attemptStartError.tone === 'plan' ? '#1E3A8A' : '#7F1D1D',
                boxShadow: '0 4px 14px rgba(15,23,42,.06)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <span style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 20,
                    border: `1px solid ${attemptStartError.tone === 'credits' ? '#FBBF24' : attemptStartError.tone === 'plan' ? '#3B82F6' : '#F87171'}`,
                  }}>
                    {attemptStartError.tone === 'credits' ? '🪙' : attemptStartError.tone === 'plan' ? '🔒' : '⚠️'}
                  </span>
                  <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-.2px' }}>{attemptStartError.title}</div>
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>{attemptStartError.message}</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={attemptStartError.onCta}
                    style={{
                      background: attemptStartError.tone === 'credits' ? '#F59E0B' : attemptStartError.tone === 'plan' ? '#3B82F6' : '#EF4444',
                      color: '#fff', border: 'none',
                      padding: '10px 20px', borderRadius: 999,
                      fontSize: 14, fontWeight: 800, cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {attemptStartError.ctaLabel}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    style={{
                      background: '#fff', color: 'inherit',
                      border: `1px solid currentColor`,
                      padding: '10px 20px', borderRadius: 999,
                      fontSize: 14, fontWeight: 700, cursor: 'pointer',
                      fontFamily: 'inherit', opacity: 0.85,
                    }}
                  >
                    Back to home
                  </button>
                </div>
              </div>
            </div>
          )}

          {!practiceLoading && !exam && (
            <div
              style={{
                minHeight: '50vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px',
              }}
            >
              <div
                style={{
                  maxWidth: '400px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '16px',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '16px',
                    background: '#EEF2FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M8 3.75h6l4.25 4.25V18a2 2 0 0 1-2 2H8A2 2 0 0 1 6 18V5.75a2 2 0 0 1 2-2Z" stroke="#4F46E5" strokeWidth="1.5" strokeLinejoin="round" />
                    <path d="M14 3.75V8h4.25" stroke="#4F46E5" strokeWidth="1.5" strokeLinejoin="round" />
                    <path d="M9.5 11.5l5 5M14.5 11.5l-5 5" stroke="#4F46E5" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#111827' }}>
                  Something went wrong
                </h2>
                <p style={{ margin: 0, fontSize: '14px', color: '#6B7280', lineHeight: 1.75 }}>
                  We&apos;re having trouble loading your exam. Please try again or contact support if the problem persists.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setReloadKey((value) => value + 1)}
                    style={{
                      background: '#4F46E5',
                      color: '#ffffff',
                      border: 'none',
                      height: '44px',
                      padding: '0 20px',
                      borderRadius: '99px',
                      fontSize: '14px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Try again
                  </button>
                  <button
                    onClick={() => window.location.href = 'mailto:support@itcplatform.com'}
                    style={{
                      background: '#ffffff',
                      color: '#374151',
                      border: '1px solid #E5E7EB',
                      height: '44px',
                      padding: '0 20px',
                      borderRadius: '99px',
                      fontSize: '14px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Contact support
                  </button>
                </div>
              </div>
            </div>
          )}

          {!practiceLoading && result && (
            <div style={{ display: 'grid', gap: '32px', maxWidth: '860px', margin: '0 auto' }}>
              {(() => {
                const response = result;

                return (
                  <div key={response.id} style={{ display: 'grid', gap: '32px' }}>
                    {response.mark_status === 'blocked' ? (
                      <div style={{ display: 'grid', gap: '18px' }}>
                        <div style={{ background: '#FEF2F2', borderLeft: '4px solid #EF4444', borderRadius: '0 12px 12px 0', padding: '18px', color: '#7F1D1D', lineHeight: 1.8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontWeight: 800, color: '#B91C1C' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 8v5m0 3h.01M10.29 3.86l-7.4 12.82A2 2 0 0 0 4.62 20h14.76a2 2 0 0 0 1.73-3.32L13.71 3.86a2 2 0 0 0-3.42 0Z" stroke="#B91C1C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            <span>{response.title || 'Time ended, attempt again!'}</span>
                          </div>
                          <div style={{ fontSize: '15px', marginBottom: '10px' }}>{response.blocked_detail}</div>
                          <div style={{ fontSize: '14px', fontWeight: 700 }}>Reason: {response.blocked_reason}</div>
                        </div>

                        <div style={{ background: '#EEF2FF', borderLeft: '4px solid #6366F1', borderRadius: '0 12px 12px 0', padding: '16px', color: '#312E81', lineHeight: 1.75 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontWeight: 700, color: '#3730A3' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.75c.55.39 1 .96 1 1.64V17h6v-.61c0-.68.45-1.25 1-1.64A7 7 0 0 0 12 2Z" stroke="#3730A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            <span>What to do next</span>
                          </div>
                          <div style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>{response.suggestion}</div>
                        </div>
                      </div>
                    ) : (
                      <FETWritingReportView
                        report={response}
                        responses={response.writing_responses || []}
                        theme={theme}
                        isMobile={isMobile}
                        isTablet={isTablet}
                        showSubmittedAnswers={showSubmittedAnswers}
                        onToggleSubmittedAnswers={() => setShowSubmittedAnswers((open) => !open)}
                      />
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {!practiceLoading && !result && visibleQuestion && (
            <div
              id="fet-question-card"
              className="fet-surface"
              style={{
                borderRadius: '16px',
                padding: '32px',
                borderLeft: `3px solid ${theme.accent}`
              }}
            >
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1.5px', color: theme.muted, fontWeight: 700, marginBottom: 10 }}>
                  Question Section
                </div>
                <h2 style={{ fontSize: '26px', lineHeight: 1.2, margin: 0, fontWeight: 600, color: theme.text }}>
                  Question {currentStep}
                </h2>
              </div>

              {!isQuestionTwo ? (
                <div>
                  <div style={{ display: 'grid', gap: '12px', marginBottom: '20px' }}>
                    {getQuestionLead(part1Question).map((line, index) => (
                      <p key={`${part1Question?.id}-lead-${index}`} className="fet-readable-text" style={{ color: theme.body, margin: 0 }}>
                        {line}
                      </p>
                    ))}
                  </div>

                  {getQuestionBullets(part1Question).length > 0 && (
                    <div style={{ marginBottom: '22px' }}>
                      <div style={{ fontSize: '15px', fontWeight: 600, color: theme.body, marginBottom: '14px' }}>
                        {part1Question?.question_type === 'email' ? 'In the email' : 'Write about'}
                      </div>
                      <ul className="fet-bullets fet-readable-list" style={{ marginBottom: '22px' }}>
                        {getQuestionBullets(part1Question).map((item, index) => <li key={`${part1Question?.id}-bullet-${index}`}>{item}</li>)}
                      </ul>
                    </div>
                  )}

                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '6px 14px',
                      borderRadius: '14px',
                      background: theme.accentSoftBg,
                      border: '1px solid transparent',
                      color: theme.accent,
                      fontWeight: 700,
                      marginBottom: '10px'
                    }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: theme.accent }} />
                    {getWordInstruction(part1Question, 'Write 40-50 words only.')}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
                    <span style={{ fontSize: '13px', color: theme.logoutText }}>Write your answer in the box below.</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', background: theme.softBadgeBg, color: theme.softBadgeText, padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 600 }}>
                      (10 Marks)
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '.12em', color: theme.muted, fontWeight: 700 }}>
                      Response
                    </span>
                    <span style={{ fontSize: '11px', color: theme.muted, fontWeight: 600 }}>
                      Word count {wordCount(answers[part1Question?.id] || '')}{part1Question?.word_count ? ` / ${part1Question.word_count}` : ''}
                    </span>
                  </div>

                  <div
                    style={{
                      border: `1.5px solid ${theme.border}`,
                      borderRadius: '10px',
                      padding: '16px',
                      minHeight: '180px',
                      background: theme.surfaceAlt
                    }}
                  >
                    <textarea
                      className="fet-textarea fet-readable-textarea"
                      value={answers[part1Question?.id] || ''}
                      onChange={(e) => updateAnswerWithLimit(part1Question, e.target.value)}
                      disabled={inputLocked}
                      style={{
                        width: '100%',
                        minHeight: isMobile ? '180px' : '220px',
                        padding: '0',
                        border: 'none',
                        resize: 'vertical',
                        background: 'transparent',
                        color: theme.text,
                        opacity: inputLocked ? 0.75 : 1,
                        cursor: inputLocked ? 'not-allowed' : 'text',
                      }}
                      placeholder="Type your email here..."
                    />
                  </div>
                </div>
              ) : (
                <div>
                  {part2Questions.map((question, index) => {
                    const isSelected = selectedPart2Id === question.id;
                    const optionTitle = getQuestionOptionTitle(question, index);
                    return (
                      <React.Fragment key={question.id}>
                        <div
                          className="fet-topic-card"
                          style={{
                            marginBottom: '24px',
                            padding: isMobile ? '18px' : '22px',
                            background: isSelected ? theme.accentSoftBg : theme.surfaceAlt,
                            border: isSelected ? `1px solid ${theme.accent}` : `1px solid ${theme.border}`,
                            borderRadius: '18px',
                            cursor: inputLocked ? 'default' : 'pointer'
                          }}
                          onClick={() => {
                            if (!inputLocked) setSelectedTopic(question.id);
                          }}
                        >
                          <h4 style={{ fontSize: '16px', fontWeight: 800, color: theme.text, margin: '0 0 14px' }}>{optionTitle}</h4>
                          {splitLines(question.instruction).map((line, lineIndex) => (
                            <p key={`${question.id}-instruction-${lineIndex}`} className="fet-readable-text" style={{ color: theme.body, margin: '0 0 10px' }}>
                              {line}
                            </p>
                          ))}
                          {question.prompt_heading && <p className="fet-readable-text" style={{ color: theme.body, margin: '0 0 12px' }}>{question.prompt_heading}</p>}
                          {question.question_type === 'story' && question.story_opener && (
                            <div className="fet-readable-box" style={{ marginBottom: '12px', padding: '12px 14px', borderRadius: '10px', background: theme.surfaceSoft, color: theme.body, fontStyle: 'italic' }}>
                              {question.story_opener}
                            </div>
                          )}
                          {question.question_type === 'email' && question.email_body && (
                            <div className="fet-readable-box" style={{ marginBottom: '12px', padding: '12px 14px', borderRadius: '10px', background: theme.surfaceSoft, color: theme.body }}>
                              {splitLines(question.email_body).map((line, bodyIndex) => (
                                <div key={`${question.id}-email-${bodyIndex}`}>{line}</div>
                              ))}
                            </div>
                          )}
                          {getQuestionBullets(question).length > 0 && (
                            <ul className="fet-bullets fet-readable-list">
                              {getQuestionBullets(question).map((item, bulletIndex) => <li key={`${question.id}-bullet-${bulletIndex}`}>{item}</li>)}
                            </ul>
                          )}
                        </div>
                        {index < part2Questions.length - 1 && (
                          <p style={{ fontSize: '13px', textAlign: 'center', fontWeight: 800, letterSpacing: '.18em', color: theme.muted, margin: '0 0 24px' }}>
                            OR
                          </p>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {selectedPart2Question && (
                    <div
                      style={{
                        display: 'grid',
                        gap: '10px',
                        marginBottom: '18px',
                        padding: isMobile ? '16px 16px' : '18px 20px',
                        borderRadius: '20px',
                        border: `2px solid ${theme.accent}`,
                        borderLeft: `6px solid ${theme.accent}`,
                        background: 'linear-gradient(135deg, rgba(79,70,229,.16), rgba(99,102,241,.10))',
                        boxShadow: '0 16px 34px rgba(79,70,229,.14)',
                      }}
                    >
                      <div style={{ fontSize: '12px', color: theme.accent, textTransform: 'uppercase', letterSpacing: '.16em', fontWeight: 900 }}>
                        Selected Question 2
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'grid', gap: '6px' }}>
                          <div style={{ fontSize: '14px', fontWeight: 800, color: theme.body }}>
                            You are currently answering this Question 2 option
                          </div>
                          <div style={{ fontSize: '19px', fontWeight: 900, color: theme.text }}>
                            {selectedPart2Question.prompt_title || selectedPart2Question.label || 'Writing option'}
                          </div>
                        </div>
                        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 12px', borderRadius: '999px', background: theme.surface, color: theme.accent, border: `1px solid ${theme.border}`, fontSize: '13px', fontWeight: 900 }}>
                          {getQuestionTypeLabel(selectedPart2Question)}
                        </span>
                      </div>
                    </div>
                  )}

                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '6px 14px',
                      borderRadius: '14px',
                      background: theme.accentSoftBg,
                      border: '1px solid transparent',
                      color: theme.accent,
                      fontWeight: 700,
                      marginBottom: '12px'
                    }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: theme.accent }} />
                    {getWordInstruction(selectedPart2Question, 'Write around 100 words.')}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '.12em', color: theme.muted, fontWeight: 700 }}>
                      Response
                    </span>
                    <span style={{ fontSize: '11px', color: theme.muted, fontWeight: 600 }}>
                      Word count {wordCount(answers[selectedPart2Question?.id] || '')}{selectedPart2Question?.word_count ? ` / ${selectedPart2Question.word_count}` : ''}
                    </span>
                  </div>

                  <div
                    style={{
                      border: `1.5px solid ${theme.border}`,
                      borderRadius: '10px',
                      padding: '16px',
                      minHeight: '180px',
                      background: theme.surfaceAlt
                    }}
                  >
                    <textarea
                      className="fet-textarea fet-readable-textarea"
                      value={answers[selectedPart2Question?.id] || ''}
                      onChange={(e) => updateAnswerWithLimit(selectedPart2Question, e.target.value)}
                      disabled={inputLocked}
                      style={{
                        width: '100%',
                        minHeight: isMobile ? '180px' : '240px',
                        padding: '0',
                        border: 'none',
                        resize: 'vertical',
                        background: 'transparent',
                        color: theme.text,
                        opacity: inputLocked ? 0.75 : 1,
                        cursor: inputLocked ? 'not-allowed' : 'text',
                      }}
                      placeholder="Type your essay here..."
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {!practiceLoading && exam && (
          <div
            className="fet-surface"
            style={{
              position: 'fixed',
              left: isMobile ? '12px' : '50%',
              transform: isMobile ? 'none' : 'translateX(-50%)',
              right: isMobile ? '12px' : 'auto',
              bottom: isMobile ? '12px' : '18px',
              width: isMobile ? 'auto' : 'min(1120px, calc(100% - 40px))',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              borderBottomLeftRadius: isMobile ? '12px' : '0',
              borderBottomRightRadius: isMobile ? '12px' : '0',
              padding: isMobile ? '14px' : '10px 18px',
              zIndex: 25,
              borderTop: `1px solid ${theme.border}`
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'minmax(260px, 1fr) auto auto',
                gap: isMobile ? '12px' : '16px',
                alignItems: 'center'
              }}
            >
              <div style={{ display: 'grid', gap: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                  <div className="fet-progress-label" style={{ fontSize: '13px', color: theme.body, fontWeight: 700 }}>
                    {result ? 'AI result ready' : 'Question progress'}
                  </div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: theme.logoutText, fontSize: '13px', fontWeight: 700 }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '54px',
                        padding: '6px 10px',
                        borderRadius: '999px',
                        background: theme.accentSoftBg,
                        color: theme.accent,
                        border: '1px solid transparent'
                      }}
                    >
                      {result ? '1 report' : `${currentStep} / ${totalSteps}`}
                    </span>
                  </div>
                </div>
                <div style={{ height: '6px', background: theme.progressTrack, borderRadius: '99px', overflow: 'hidden' }}>
                  <div
                    style={{
                      width: `${result ? 100 : progressPercent}%`,
                      height: '100%',
                      background: result ? '#10B981' : theme.accent,
                      borderRadius: '99px'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', width: isMobile ? '100%' : 'auto', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                <div
                  ref={fontPopoverRef}
                  style={{
                    position: 'relative',
                    width: isMobile ? 'calc(50% - 5px)' : 'auto'
                  }}
                >
                  {fontPopoverOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '72px',
                        left: 0,
                        width: '240px',
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB',
                        background: '#ffffff',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                        padding: '16px',
                        zIndex: 35,
                      }}
                    >
                      {[
                        {
                          label: 'Text size',
                          value: textSize,
                          onChange: setTextSize,
                          options: [
                            ['small', 'A−'],
                            ['normal', 'A'],
                            ['large', 'A+'],
                          ],
                        },
                        {
                          label: 'Font style',
                          value: fontStyle,
                          onChange: setFontStyle,
                          options: [
                            ['default', 'Default'],
                            ['serif', 'Serif'],
                            ['dyslexic', 'Dyslexic'],
                          ],
                        },
                        {
                          label: 'Line spacing',
                          value: lineSpacing,
                          onChange: setLineSpacing,
                          options: [
                            ['compact', 'Compact'],
                            ['normal', 'Normal'],
                            ['relaxed', 'Relaxed'],
                          ],
                        },
                      ].map((section, sectionIndex) => (
                        <div key={section.label} style={{ display: 'grid', gap: '8px', marginBottom: sectionIndex === 2 ? 0 : '16px' }}>
                          <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '.12em', color: '#9CA3AF', fontWeight: 700 }}>
                            {section.label}
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {section.options.map(([value, label]) => {
                              const active = section.value === value;
                              return (
                                <button
                                  key={value}
                                  onClick={() => section.onChange(value)}
                                  style={{
                                    flex: 1,
                                    height: '36px',
                                    borderRadius: '8px',
                                    border: `1px solid ${active ? '#4F46E5' : '#E5E7EB'}`,
                                    background: active ? '#4F46E5' : '#ffffff',
                                    color: active ? '#ffffff' : '#374151',
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                  }}
                                >
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    className="fet-nav-btn"
                    onClick={() => setFontPopoverOpen((open) => !open)}
                    style={{
                      background: fontPopoverOpen ? theme.accent : 'transparent',
                      color: fontPopoverOpen ? '#ffffff' : theme.logoutText,
                      border: fontPopoverOpen ? 'none' : '1px solid transparent',
                      padding: '12px 10px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      width: '100%'
                    }}
                  >
                    🔤 FONT
                  </button>
                </div>
                <button
                  className="fet-nav-btn"
                  onClick={() => setNavDrawerOpen(true)}
                  style={{
                    background: navDrawerOpen ? theme.accentSoftBg : 'transparent',
                    color: navDrawerOpen ? theme.accent : theme.body,
                    border: `1px solid ${navDrawerOpen ? theme.accent : theme.border}`,
                    padding: '12px 16px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    width: isMobile ? 'calc(50% - 5px)' : 'auto'
                  }}
                >
                  ⇧ Question navigation
                </button>
              </div>

              <div style={{ display: 'flex', gap: '12px', width: isMobile ? '100%' : 'auto' }}>
                {result ? (
                  <>
                    <button
                      className="fet-action-btn"
                      onClick={handleTryAgain}
                      style={{
                        background: theme.accent,
                        color: '#ffffff',
                        border: 'none',
                        padding: '0 24px',
                        height: '48px',
                        borderRadius: '999px',
                        fontSize: '14px',
                        fontWeight: 800,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        flex: isMobile ? 1 : 'none'
                      }}
                    >
                      <span style={{ fontSize: '16px', lineHeight: 1 }}>↺</span>
                      <span>Start new attempt</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="fet-nav-btn"
                      onClick={() => setCurrentQuestion(16)}
                      disabled={isFirstStep}
                      style={{
                        background: 'transparent',
                        color: isFirstStep ? theme.muted : theme.logoutText,
                        border: 'none',
                        padding: '13px 18px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 700,
                        cursor: isFirstStep ? 'not-allowed' : 'pointer',
                        opacity: isFirstStep ? 0.75 : 1,
                        flex: isMobile ? 1 : 'none'
                      }}
                    >
                      &lt; Previous
                    </button>
                    {!isLastStep ? (
                      <button
                        className="fet-action-btn"
                        onClick={() => setCurrentQuestion(17)}
                        disabled={!currentQuestionAnswered}
                        title={!currentQuestionAnswered ? 'Write your answer before moving to the next question.' : ''}
                        style={{
                          background: currentQuestionAnswered ? theme.accent : theme.surfaceSoft,
                          color: currentQuestionAnswered ? '#ffffff' : theme.muted,
                          border: 'none',
                          padding: '0 24px',
                          height: '44px',
                          borderRadius: '999px',
                          fontSize: '14px',
                          fontWeight: 800,
                          cursor: currentQuestionAnswered ? 'pointer' : 'not-allowed',
                          flex: isMobile ? 1 : 'none'
                        }}
                      >
                        Next &gt;
                      </button>
                    ) : (
                      <button
                        className="fet-action-btn"
                        onClick={() => handleSubmit()}
                        disabled={submitting || !attemptId || !currentQuestionAnswered}
                        title={!currentQuestionAnswered ? 'Write your answer before submitting.' : ''}
                        style={{
                          background: (currentQuestionAnswered && attemptId && !submitting) ? theme.accent : theme.surfaceSoft,
                          color: (currentQuestionAnswered && attemptId && !submitting) ? '#ffffff' : theme.muted,
                          border: 'none',
                          padding: '0 24px',
                          height: '44px',
                          borderRadius: '999px',
                          fontSize: '14px',
                          fontWeight: 800,
                          cursor: (currentQuestionAnswered && attemptId && !submitting) ? 'pointer' : 'not-allowed',
                          opacity: (currentQuestionAnswered && attemptId && !submitting) ? 1 : 0.7,
                          flex: isMobile ? 1 : 'none'
                        }}
                      >
                        {submitting ? 'Generating Report…' : !attemptId ? (attemptStartError ? 'Cannot start' : 'Preparing…') : timeExpired ? 'Submit Report' : 'Submit'}
                      </button>
                    )}
                  </>
                )}
              </div>
              {!result && !currentQuestionAnswered && visibleQuestion && (
                <div style={{
                  marginTop: 10,
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: '#FEF3C7', border: '1px solid #FDE68A',
                  color: '#92400E', borderRadius: 12,
                  padding: '10px 14px',
                  fontSize: 13, fontWeight: 700,
                }}>
                  <span aria-hidden="true">👉</span>
                  <span>Write your answer above to continue {isLastStep ? 'and submit your exam.' : 'to the next question.'}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {!practiceLoading && !result && timeExpired && !submitting && (
          <div style={{ maxWidth: '860px', margin: '0 auto 24px' }}>
            <div style={{ background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: '14px', padding: '16px 18px', color: '#92400E', lineHeight: 1.7 }}>
              <div style={{ fontWeight: 800, marginBottom: '6px' }}>Time completed</div>
              <div>You can no longer edit your answers. Review your work and click <strong>Submit Report</strong> to generate the result.</div>
            </div>
          </div>
        )}

        {submitting && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.55)',
              zIndex: 45,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px',
            }}
          >
            <div
              style={{
                width: 'min(460px, 100%)',
                borderRadius: '28px',
                background: isDark ? 'linear-gradient(180deg, #0F172A 0%, #111827 100%)' : 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
                border: `1px solid ${theme.border}`,
                boxShadow: theme.shadow,
                padding: isMobile ? '28px 22px' : '34px 32px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: '72px',
                  height: '72px',
                  margin: '0 auto 18px',
                  borderRadius: '999px',
                  display: 'grid',
                  placeItems: 'center',
                  background: isDark ? 'rgba(99,102,241,.18)' : 'rgba(79,70,229,.10)',
                }}
              >
                <div
                  style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '999px',
                    border: `3px solid ${isDark ? 'rgba(165,180,252,.25)' : 'rgba(99,102,241,.22)'}`,
                    borderTopColor: theme.accent,
                    animation: 'fetSpin 0.9s linear infinite',
                  }}
                />
              </div>
              <div style={{ fontSize: '11px', color: theme.accent, textTransform: 'uppercase', letterSpacing: '.18em', fontWeight: 800, marginBottom: '10px' }}>
                AI Marking
              </div>
              <h3 style={{ margin: 0, fontSize: isMobile ? '24px' : '28px', fontWeight: 800, color: theme.text, letterSpacing: '-0.02em' }}>
                {submissionPhase === 'submitting' ? 'Submitting your answers' : 'Result Report is generating'}
              </h3>
              <p style={{ margin: '12px auto 0', maxWidth: '320px', fontSize: '15px', lineHeight: 1.75, color: theme.body }}>
                {submissionPhase === 'submitting'
                  ? 'Please wait while we securely send your answers for marking.'
                  : 'Please stay on this page while we prepare your FET writing report.'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FETLandingPage;
